import type { Gasto, ListadoGastos, PropiedadGasto, DatosGasto } from '@/components/gastos/tipos';
import type { Peticion } from '@/lib/peticiones';
import type {
  DatosSensibles,
  Empleado,
  EmpleadoSinFicha,
  FichaEmpleado,
  FilaAusencias,
  ListadoPersonal,
  ResumenFichajes,
  SaldoVacaciones,
  Vencimiento,
} from '@/lib/personal';
import type {
  CambioEstadoCandidato,
  Candidato,
  ContadoresCandidatos,
  DuplicadoCandidato,
  ViaRenovacion,
} from '@/lib/candidatos';
import type {
  AccionDispositivo,
  AuditoriaDispositivos,
  ContadorAtencion,
  Dispositivo,
  EntradaHistorial,
  FichaDispositivo,
  FilaDispositivo,
  InformeMigracion,
  ListadoDispositivos,
  OpcionPropiedad,
  ResultadoSincronizacion,
  ResumenIncidencias,
  ResumenStock,
} from '@/lib/dispositivos';
import type {
  AjustesFirma, ClavePlantilla, Condiciones, Contrato, ContratoLigero, DiffPlantilla, DocumentoResuelto, EstadoContrato,
  FirmanteEntrada, ModeloPlantilla, PlantillaVersion, Preajuste, PreparacionConsulta, PreparacionFirma, PropuestaProcesado, RecordatoriosConfig,
  RespuestaParte, ResultadoProcesado, TipoParte,
} from '@/lib/contratos';

import type {
  AjustesComunicaciones, Audiencia, Baja as BajaComunicaciones, Campana, CampanaEditable, Canal, Conversacion as ConversacionWhatsApp,
  BorradorPlantilla, Envio as EnvioCampana, EstadoWhatsApp, FilaEnlace, HiloWhatsApp, Idioma, MensajeWhatsApp, PlantillaResumen, PreviaWhatsApp, WhatsAppDeLead, ProcesoConversacion, PropuestaConversacion,
  Recuento, Revision, TipoDestinatario, Version, VistaPrevia, WhatsAppCampana,
} from '@/lib/comunicaciones';

import { getSessionToken, renovarSesion, setSessionToken } from '@/lib/sesion';
import type { ConversacionSerenaDetalle, ConversacionSerenaResumen } from '@/lib/serena';

const API_BASE = '/api';

// Aquí vivían; se reexportan por si algo los sigue importando de api.
export { getSessionToken, setSessionToken };

/**
 * `fetch` contra la API con la sesión puesta. Los GET llevan la cookie de Easy
 * Auth, que es la que inyecta `x-ms-client-principal`; las escrituras van sin
 * ella (Easy Auth las rechaza con cookie) y solo con el token de sesión.
 *
 * Un 401 casi siempre es un token que el servidor ya no conoce porque se ha
 * reiniciado en un despliegue: se pide uno nuevo y se repite la petición una
 * vez. Repetir es seguro porque el 401 lo da el middleware antes de que la
 * ruta haga nada. Si la sesión de Microsoft también ha caducado, no se repite:
 * sale la pantalla de login.
 */
async function fetchConSesion(path: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method || 'GET').toUpperCase();
  const isWriteRequest = method !== 'GET' && method !== 'HEAD';
  const enviar = () => {
    const headers: Record<string, string> = { ...(init.headers as Record<string, string> || {}) };
    const token = getSessionToken();
    if (token) headers['X-Session-Token'] = token;
    return fetch(`${API_BASE}${path}`, {
      ...init,
      headers,
      credentials: isWriteRequest ? 'omit' : 'include',
    });
  };

  const res = await enviar();
  if (res.status !== 401) return res;
  // Si no se puede preguntar por la sesión, el 401 se queda como error normal.
  const viva = await renovarSesion().catch(() => null);
  if (viva === null) return res;
  if (!viva) throw new Error('La sesión ha caducado. Vuelve a entrar.');
  return enviar();
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };

  // For non-multipart requests, set JSON content type
  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetchConSesion(path, { ...options, headers });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({ error: 'Error desconocido' }));
    throw new Error(errorBody.message || errorBody.error || `HTTP ${res.status}`);
  }

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('json')) {
    return res.json();
  }

  return undefined as T;
}

export interface LineaFacturaHolded {
  codigo: string;
  nombre: string;
  descripcion: string;
  origen: string;
  base: number;
  iva: number;
  total: number;
  servicioEnHolded: boolean;
}

/** Un PDF subido como adjunto a un documento de Holded. */
export interface AdjuntoHolded {
  nombre: string;
  subidoAt: string;
  subidoPor?: string;
}

export interface FacturaHoldedCreada {
  id: string;
  docNumber: string | null;
  total: number | null;
  draft: boolean;
  cuenta: 'test' | 'prod';
  createdAt: string;
  approvedAt?: string;
  /** La liquidación en PDF, adjunta a la factura */
  adjunto?: AdjuntoHolded;
}

/** La factura del propietario registrada en Holded como compra (explotación). */
export interface CompraHoldedCreada {
  id: string;
  numero: string | null;
  total: number | null;
  draft: boolean;
  cuenta: 'test' | 'prod';
  contactId?: string;
  createdAt: string;
  approvedAt?: string;
  /** La factura del propietario en PDF, adjunta a la compra */
  adjunto?: AdjuntoHolded;
}

export type CompraHoldedPreview =
  | { aplica: false; motivo: string; creada: null }
  | { aplica: true; creada: CompraHoldedCreada }
  | {
      aplica: true;
      creada: null;
      cuenta: 'test' | 'prod';
      numero: string | null;
      fecha: string;
      concepto: string;
      base: number;
      iva: number;
      irpf: number;
      total: number;
      totalFactura: number;
      diferencia: number;
      bloqueo: string | null;
      contacto: { nombre: string; documento: string; origen: 'ficha' | 'dni' | 'nuevo' | null; nombreEnHolded: string | null };
      /** Una compra con ese número que ya está en Holded */
      duplicada: { id: string; numero: string } | null;
    };

export type FacturaHoldedPreview =
  | { aplica: false; motivo: string; creada: FacturaHoldedCreada | null }
  | {
      aplica: true;
      cuenta: 'test' | 'prod';
      fecha: string;
      serie: string | null;
      /** Año cuya serie falta en Holded; mientras falte no se puede crear */
      serieFalta?: string | null;
      contacto: { nombre: string; documento: string; direccion: string; email: string; enHolded: boolean; nombreEnHolded: string | null };
      lineas: LineaFacturaHolded[];
      subtotal: number;
      iva: number;
      total: number;
      cargosLiquidacion: number | null;
      diferencia: number | null;
      creada: FacturaHoldedCreada | null;
    };

export interface AperturasResumen {
  from: string; to: string; spanDays: number;
  totalsByCategory: Record<string, number>;
  totalsByTrigger: Record<string, number>;
  heatmap: { weekday: number; hour: number; total: number; media: number }[];
  series: { day: string; apertura: number; cierre: number }[];
  hours: Record<string, number>[];
  devices: { smartlockId: number; deviceLabel: string; n: number }[];
  sensor: {
    devices: { smartlockId: number; deviceLabel: string; aperturas: number; medianaSegundos: number | null }[];
    p50: number | null; p90: number | null; muestras: number;
  };
}

export interface AperturasLlegadas {
  from: string; to: string; total: number;
  bins: { bin: number; etiqueta: string; llegadas: number }[];
  p25: string | null; p50: string | null; p90: string | null;
  porDia: { day: string; llegadas: number }[];
  porApartamento: { label: string; llegadas: number }[];
}

export interface AperturasManuales {
  from: string; to: string;
  serieMeses: (Record<string, number> & { month: string; total: number; noAtribuibles: number; pctNoAtribuible: number })[];
  ranking: (Record<string, number> & { smartlockId: number; deviceLabel: string; total: number; pctNoAtribuible: number })[];
  heatmap: { weekday: number; hour: number; total: number; media: number }[];
  rechazos: {
    total: number;
    porDispositivo: { smartlockId: number; deviceLabel: string; n: number }[];
    porHora: { hour: number; rechazos: number }[];
  };
}

export interface AperturasSalud {
  from: string; to: string;
  dispositivos: {
    smartlockId: number; deviceLabel: string; deviceKind: string;
    ultimoEvento: string; diasSinEventos: number; eventosEnRango: number; tieneSensor: boolean;
  }[];
  silenciosos: AperturasSalud['dispositivos'];
  fallos: { smartlockId: number; deviceLabel: string; total: number; estados: Record<string, number> }[];
  alertas: { smartlockId: number; deviceLabel: string; actionLabel: string; n: number }[];
  sensor: { conSensor: number; totalDispositivos: number; dispositivos: { smartlockId: number; deviceLabel: string; n: number }[] };
}

export interface AperturasTimbres {
  from: string; to: string; spanDays: number;
  totals: { timbre: number; keypad: number; remoto: number };
  byDevice: {
    smartlockId: number; deviceLabel: string;
    timbre: number; keypad: number; remoto: number; total: number;
    timbresPorDia: number;
    /** Rings per identified opening; null when there is nothing identified to compare against. */
    ratioTimbre: number | null;
  }[];
  series: (Record<string, number | string> & { day: string; total: number })[];
  hours: { hour: number; timbres: number }[];
}

export interface LlamadasResumen {
  periodo: { from: string; to: string };
  total: number;
  duracion: { media: number | null; mediana: number | null; p90: number | null; sinDato: number };
  colgadas: { total: number; pct: number };
  fueraHorario: { total: number; pct: number };
  identificadas: { total: number; pct: number };
  /** `total` son las transcribibles: excluye las que no tienen grabación. */
  transcripciones: {
    listas: number;
    /** Pendientes en total: las en blanco más las que solo esperan mejor calidad. */
    pendientes: number;
    /** De las pendientes, las que ya tienen texto del respaldo de Transcribe. */
    porMejorar: number;
    sinGrabacion: number;
    total: number;
  };
  telefonosRecurrentes: number;
  analisis: {
    analizadas: number;
    resueltas: number;
    pctResueltas: number;
    categorias: { nombre: string; total: number }[];
    sentimientos: { nombre: string; total: number }[];
  };
  heatmap: { weekday: number; hour: number; total: number; media: number }[];
  serie: { day: string; total: number }[];
  alojamientos: { nombre: string; total: number }[];
}

/** Las mismas etiquetas que usa Amazon Comprehend, por si algún día se compara. */
export type Sentimiento = 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL' | 'MIXED';

export interface LlamadaResumida {
  id: string;
  startedAt: string;
  phone: string | null;
  durationSec: number | null;
  guestName: string | null;
  listingNickname: string | null;
  transcriptStatus: string | null;
  categoria: string | null;
  sentimientoGlobal: Sentimiento | null;
  resuelto: boolean | null;
  /** Marcada para guardar. La marca vive en un contenedor propio del CRM. */
  guardada?: boolean;
  /** 'saliente' si la hicimos nosotros desde la centralita. */
  direccion?: 'entrante' | 'saliente' | null;
  /**
   * Lo que quien llamaba dijo de sí mismo, leído de la transcripción. Solo se
   * usa cuando su número no está fichado en Hostify, y se marca como tal: es
   * una lectura de lo que dijo, no un dato verificado.
   */
  nombreDicho?: string | null;
  alojamientoDicho?: string | null;
  /**
   * Qué campos se han escrito a mano. Los corregidos mandan sobre lo que diga
   * Hostify, y sobre todo sobreviven a que el pipeline reprocese la llamada.
   */
  corregido?: { guestName: boolean; listingNickname: boolean };
  /**
   * Su ficha en Clientes › Huéspedes, cuando el teléfono se reconoce en la base
   * unificada. Null si no se le ha podido poner cara, y entonces el nombre —si
   * lo hay— no se enlaza.
   */
  fichaHuesped?: string | null;
}

/** Una llamada guardada trae además cuándo y quién la marcó. */
export interface LlamadaGuardada extends LlamadaResumida {
  guardadaEn: string;
  guardadaPor: string;
  nota: string | null;
}

export interface LlamadasGuardadas {
  total: number;
  items: LlamadaGuardada[];
}

export interface LlamadasListado {
  total: number;
  page: number;
  pageSize: number;
  items: LlamadaResumida[];
}

export interface TurnoLlamada {
  t: number;
  quien: 'huesped' | 'sereno';
  texto: string;
}

export interface LlamadaDetalle extends LlamadaResumida {
  yearMonth: string;
  audioKey: string | null;
  transcript: string | null;
  dialogue: TurnoLlamada[] | null;
  transcriptSource: string | null;
  intencion: string | null;
  /** Un punto por intervención del huésped, con su segundo dentro de la llamada. */
  sentimientoPorTurno: { t: number; i: number; s: Sentimiento }[] | null;
  updatedAt: string;
  /** Null mientras no se haya procesado para ningún lead. */
  lead: EnlaceLead | null;
}

/** A qué lead se procesó una llamada, si se procesó. */
export interface EnlaceLead {
  id: string;
  leadId: string;
  leadNombre: string;
  procesadaEn: string;
  procesadaPor: string;
  modelo: string;
  rellenados: string[];
  actividadId: string;
}

/** Lo que el modelo saca de la conversación. Propone: no escribe nada por su cuenta. */
export interface LecturaLlamada {
  resumen: string;
  puntosClave: string[];
  objeciones: string[];
  siguientePaso: string | null;
  temperatura: 'fria' | 'templada' | 'caliente' | null;
  faseSugerida: { id: string; porQue: string } | null;
  campos: Record<string, string | number>;
  modelo: string;
  at: string;
}

export interface ResultadoProcesarLead {
  lectura: LecturaLlamada;
  /** Campos de la ficha que estaban vacíos y se han rellenado. */
  rellenados: string[];
  enlace: EnlaceLead;
  fases: { id: string; nombre: string }[];
}

export interface LlamadasBusqueda {
  total: number;
  items: (Omit<LlamadaResumida, 'transcriptStatus'> & { fragmento: string })[];
}

export interface Contacto {
  id: string;
  nombre: string;
  telefono: string;
  telefonoAlt: string | null;
  categoria: 'propietario' | 'lead_propietario' | 'huesped';
  detalle: string | null;
  ficha: string;
}

export interface Agenda {
  total: number;
  porCategoria: Partial<Record<Contacto['categoria'], number>>;
  items: Contacto[];
}


// ── Huéspedes (clientes unificados) ──
//
// Un huésped aquí es una PERSONA, no una reserva ni una ficha de Hostify: el
// PMS crea una ficha por reserva de canal —Booking se inventa un email nuevo
// cada vez— y `guest_customers` las junta por teléfono. De ahí que un cliente
// pueda tener varias fichas (`fichas`) y varias grafías del nombre (`names`).

export interface HuespedResumen {
  generadoEn: string;
  /** Estancias cuya ENTRADA aún no ha llegado: comprometido, no cobrado. */
  estanciasPorLlegar: number;
  /** Huéspedes alojados ahora mismo. */
  estanciasEnCurso: number;
  ingresoPorLlegar: number;
  /** «Repetidores» cuya segunda estancia todavía no ha ocurrido. */
  repetidoresPorLlegar: number;
  clientes: number;
  /** Marcados a mano para no contar. No entran en ninguna cifra de arriba. */
  excluidos: number;
  conEstancia: number;
  repetidores: number;
  pctRepetidores: number;
  /** Clientes formados juntando más de una ficha de Hostify. */
  fusionados: number;
  /** Nombres dispares dentro del mismo cliente: probablemente familia o pareja. */
  revisar: number;
  conLlamadas: number;
  conResena: number;
  ingresoTotal: number;
  ingresoRepetidores: number;
  pctIngresoRepetidores: number;
  valorMedio: number;
  valorMedioRepetidor: number;
  /** Sobre 5, ya normalizada: Booking puntúa sobre 10 y Airbnb sobre 5. */
  ratingMedio: number | null;
  canales: { nombre: string; total: number }[];
  alojamientos: { nombre: string; total: number }[];
  paises: { nombre: string; total: number }[];
  cohortes: { anyo: string; clientes: number; repiten: number; pct: number }[];
}

export interface HuespedFila {
  id: string;
  name: string;
  names: string[];
  phone: string;
  emails: string[];
  country: string | null;
  city: string | null;
  language: string | null;
  channels: string[];
  matchedBy: 'telefono' | 'email' | 'ninguno';
  fichas: number;
  stays: number;
  cancelled: number;
  nights: number;
  revenue: number;
  currency: string | null;
  firstCheckIn: string | null;
  lastCheckOut: string | null;
  listings: string[];
  sources: string[];
  isRepeat: boolean;
  rating: number | null;
  reviews: number;
  llamadas: number;
  ultimaLlamada: string | null;
  llamadaNegativa: boolean;
  revisar: boolean;
  excluido: boolean;
  excluidoPor?: string;
  excluidoEn?: string;
  motivo?: string;
}

export interface HuespedesListado {
  total: number;
  page: number;
  pageSize: number;
  generadoEn: string;
  items: HuespedFila[];
}

export interface EstanciaHuesped {
  reservationId: string;
  guestId: string | null;
  confirmationCode: string | null;
  status: string | null;
  source: string | null;
  listingNickname: string | null;
  checkIn: string | null;
  checkOut: string | null;
  nights: number | null;
  guests: number | null;
  currency: string | null;
  totalPrice: number | null;
  payout: number | null;
}

export interface ResenaHuesped {
  reviewId: number;
  reservationId: string;
  propertyName: string | null;
  platform: string | null;
  /** Tal y como la puso el canal, en su escala. */
  ratingOriginal: number | null;
  /** La misma nota llevada a base 5, que es lo comparable. */
  rating: number | null;
  comments: string | null;
  created: string | null;
}

export interface LlamadaHuesped {
  id: string;
  startedAt: string;
  durationSec: number | null;
  categoria: string | null;
  sentimientoGlobal: Sentimiento | null;
  resuelto: boolean | null;
  direccion: string | null;
}

export interface HuespedDetalle {
  id: string;
  matchedBy: 'telefono' | 'email' | 'ninguno';
  name: string;
  names: string[];
  phone: string;
  emails: string[];
  /** Las fichas de Hostify que hay detrás. Sirven para buscar en el PMS. */
  guestIds: string[];
  country: string | null;
  city: string | null;
  language: string | null;
  channels: string[];
  stats: {
    reservations: number; confirmed: number; cancelled: number; nights: number;
    revenue: number; currency: string | null; firstCheckIn: string | null;
    lastCheckOut: string | null; listings: string[]; sources: string[]; isRepeat: boolean;
  };
  revisar: boolean;
  excluido: boolean;
  updatedAt: string;
  stays: EstanciaHuesped[];
  reviews: ResenaHuesped[];
  calls: LlamadaHuesped[];
}

// ─── Copy (textos de los anuncios) ────────────────────────────────────────

/**
 * Los campos de texto de un anuncio, en el orden en que se editan.
 *
 * Sin `description`: Hostify lo mantiene en su API pero no lo enseña en ninguna
 * pantalla, y Airbnb dejó de usarlo al pasar a los campos estructurados. Escribir
 * ahí era gastar una llamada al modelo en un texto que no se publica en ningún
 * sitio. El contenido que ya existe en Hostify se queda como está: dejamos de
 * mandarlo, no lo borramos.
 */
export const COPY_FIELDS = [
  'name', 'summary', 'space', 'interaction', 'notes',
  'neighborhood_overview', 'house_rules', 'house_manual', 'checkin_place',
  'arrival_info', 'transit', 'access', 'directions',
] as const;

export type CopyField = (typeof COPY_FIELDS)[number];

/** Textos de un anuncio en un idioma. Hostify deja campos sueltos sin rellenar. */
export type CopyTexts = Partial<Record<CopyField, string>>;

export interface CopyListing {
  id: number;
  nickname?: string;
  name?: string;
  city?: string;
  country?: string;
  [key: string]: any;
}

/** Una traducción tal y como la quiere Hostify: el idioma junto a los campos. */
export type CopyTranslationPayload = CopyTexts & { lang: string };

export interface CopyTranslateFieldResult {
  success: boolean;
  translations: Record<string, string>;
  /** Idiomas que sí quedaron guardados en Hostify. */
  saved: string[];
  errors: { lang: string; error: string }[];
}

export interface CopyTranslateAllResult {
  success: boolean;
  results: Record<string, CopyTexts>;
  saved: string[];
  errors: { lang: string; error: string }[];
}

export interface CopyPrompt {
  value: string;
  /** true si alguien lo ha editado; si no, `value` es igual que `default`. */
  is_custom: boolean;
  default: string;
}

export type CopyAction = 'generate_field' | 'generate_all' | 'translate_field' | 'generate_title';

export interface CopyHistoryEntry {
  id: string;
  action: CopyAction;
  language: string;
  field_key: string | null;
  fields: Record<string, string>;
  created_at: string;
}


// ─── Analytics > Reseñas ─────────────────────────────────────────────────────
//
// Las notas viajan por duplicado a propósito: `nota5` es la normalizada a la
// escala de 5 —con la que se compara y se ordena— y `nativa`/`notaNativa` la
// que puso el canal, que es la que el propietario reconoce cuando abre la
// extranet de Booking. En pantalla mandan las de 5; la nativa se enseña al lado
// en gris, nunca dentro de un tooltip: en un móvil no hay hover.

export type CanalResena = 'airbnb' | 'booking' | 'vrbo' | 'otras';
export type RangoResenas = '3m' | '6m' | '12m' | '24m' | 'todo';
export type SeveridadResena = 'alta' | 'media' | 'baja';
export type EstadoPlan = 'pendiente' | 'en_curso' | 'resuelto' | 'descartado';
export type BandaPlan = 'critico' | 'revisar' | 'menor';
/** `pocas` = menos de 8 notas · `sin_nota` · `edificio` = reseñas del padre. */
export type BadgeListing = 'pocas' | 'sin_nota' | 'edificio';

export interface NotaCanal {
  canal: CanalResena; etiqueta: string; escala: 5 | 10;
  n: number; nota5: number | null; nativa: number | null;
}

export interface SubnotaAgregada {
  clave: string; etiqueta: string; tema: string;
  /** Solo limpieza, ubicación y calidad-precio las puntúan los dos canales. */
  comun: boolean;
  canales: CanalResena[];
  n: number;
  /** null con menos de 3 opiniones: no se pinta barra, se escribe un guion. */
  sobre5: number | null;
}

export type Reparto = [number, number, number, number, number];

export interface FichaListing {
  listingId: string;
  /** Como está en Hostify: «BBT|Currican 11». */
  propiedad: string;
  /** Sin el prefijo de zona: «Currican 11». */
  nombre: string;
  zona: string | null;
  /** La media real. Es lo que SE ENSEÑA. */
  nota5: number | null;
  /** Media bayesiana. Solo ORDENA; no se pinta nunca. */
  score: number;
  nResenas: number; nConNota: number;
  reparto: Reparto;
  porCanal: NotaCanal[];
  peorSubnota: { clave: string; etiqueta: string; sobre5: number } | null;
  delta: number | null;
  temasAbiertos: number; criticos: number;
  ultima: string | null;
  badges: BadgeListing[];
}

export interface ResumenResenas {
  rango: RangoResenas; desde: string | null; generado: string;
  global: {
    nota5: number | null; nResenas: number; nConNota: number; delta: number | null;
    porCanal: NotaCanal[]; reparto: Reparto;
    serie: { mes: string; n: number; nota5: number | null }[];
    subnotas: SubnotaAgregada[];
    peorSubnota: { clave: string; etiqueta: string; sobre5: number; vsMedia: number } | null;
  };
  alerta: { criticos: number; pisosCriticos: number; abiertos: number };
  zonas: { zona: string; nota5: number | null; nListings: number; nResenas: number }[];
  listings: FichaListing[];
  catalogoZonas: string[];
}

/** Un texto de reseña, ya resuelto: `texto` es el español si lo hay. */
export interface BloqueTexto { texto: string; original: string | null; traducido: boolean }

export interface ResenaItem {
  id: string; fecha: string | null;
  temporada: string; temporadaEtiqueta: string;
  canal: CanalResena; canalEtiqueta: string; escala: 5 | 10;
  huesped: string | null; reservaId: number | null;
  nota5: number | null; notaNativa: number | null;
  titulo: string | null;
  comentario: BloqueTexto | null;
  /** Booking: «Lo que más gustó». */
  love: BloqueTexto | null;
  /** Booking: «Lo que se podría mejorar». */
  improve: BloqueTexto | null;
  /** Airbnb: la nota privada al anfitrión. */
  feedback: BloqueTexto | null;
  respuesta: string | null;
  idioma: string | null;
  subnotas: { clave: string; etiqueta: string; nativa: number; sobre5: number | null; floja: boolean }[];
  severidad: SeveridadResena | null;
  temas: { clave: string; etiqueta: string }[];
  fragmentos: string[];
  enPlan: { tema: string; etiqueta: string; estado: EstadoPlan }[];
}

export interface DetalleListing {
  rango: RangoResenas; desde: string | null;
  ficha: {
    listingId: string; propiedad: string; nombre: string; zona: string | null;
    nota5: number | null; nResenas: number; nConNota: number;
    reparto: Reparto; porCanal: NotaCanal[];
    primera: string | null; ultima: string | null;
    temasAbiertos: number; criticos: number;
    badges: BadgeListing[];
  };
  subnotas: SubnotaAgregada[];
  serie: { mes: string; n: number; nota5: number | null }[];
  temporadas: { clave: string; etiqueta: string; n: number; nota5: number | null }[];
  contadores: { todas: number; negativas: number; texto: number };
  total: number; offset: number; limite: number;
  resenas: ResenaItem[];
}

export interface CitaPlan {
  reviewId: string;
  /** Los trozos que hablan de ESTE tema, no la reseña entera. */
  fragmentos: string[];
  fecha: string | null;
  canal: CanalResena; canalEtiqueta: string; escala: 5 | 10;
  huesped: string | null;
  nota5: number | null; notaNativa: number | null;
  origen: string | null;
  texto: string | null; textoOriginal: string | null;
  idioma: string | null;
  descartada: boolean;
}

export interface PuntoPlan {
  /** `${listingId}:${tema}`. */
  id: string;
  listingId: string; propiedad: string; nombre: string; zona: string | null;
  tema: string; temaEtiqueta: string;
  /** Ubicación y precio informan, no se arreglan: van aparte. */
  accionable: boolean;
  estado: EstadoPlan;
  severidad: SeveridadResena;
  prioridad: number; banda: BandaPlan;
  menciones: number; menciones90d: number;
  reincidencia: number;
  /** Ha vuelto después de darse por resuelto. */
  recaida: boolean;
  primera: string | null; ultima: string | null;
  /**
   * La mención que se cita en la tarjeta: la más reciente cuyo texto habla de
   * este tema. `null` si el tema sale solo de las notas y nadie lo escribió.
   */
  destacada: {
    reviewId: string; texto: string; fecha: string | null;
    canal: CanalResena; canalEtiqueta: string; escala: 5 | 10;
    nota5: number | null; notaNativa: number | null;
  } | null;
  /** Las 10 menciones más recientes, más todas las descartadas. */
  citas: CitaPlan[];
  /** Cuántas hay en realidad: `citas` viene recortada para no pesar. */
  citasTotales: number;
  nota: string | null;
  actualizadoEn: string | null; actualizadoPor: string | null;
}

export interface ZonaPlan {
  zona: string;
  puntos: number;
  abiertos: number;
  criticos: number;
  pisos: number;
}

export interface PlanMejora {
  rango: RangoResenas; desde: string | null;
  contadores: Record<EstadoPlan, number>;
  /** Reparto por zona, contado SIN el filtro de zona para poder saltar entre ellas. */
  zonas: ZonaPlan[];
  /** Recuento por banda con el filtro de estado puesto, para rotular los chips. */
  porBanda: Record<BandaPlan, number>;
  total: number; offset: number; limite: number;
  abiertos: number; criticos: number; pisos: number;
  temas: { tema: string; etiqueta: string; n: number; abiertos: number }[];
  items: PuntoPlan[];
  /** Ubicación y precio, recortados igual que `items`. */
  noAccionables: PuntoPlan[];
  totalNoAccionables: number;
}

// ─── Auditoría de un alojamiento ────────────────────────────────────────────
// La escribe el mismo despliegue de Azure OpenAI que usa Propiedades > Copy.

export type PrioridadAccion = 'alta' | 'media' | 'baja';
/** `rapido` se hace esta semana; `obra` necesita presupuesto. */
export type EsfuerzoAccion = 'rapido' | 'medio' | 'obra';

export interface AccionAuditoria {
  titulo: string;
  detalle: string;
  tema: string;
  prioridad: PrioridadAccion;
  esfuerzo: EsfuerzoAccion;
  evidencia: string;
}

export interface Auditoria {
  diagnostico: string;
  acciones: AccionAuditoria[];
  puntosFuertes: string[];
  /** Solo viene con texto si el material era escaso. */
  aviso: string;
}

/** La huella del material: con qué se escribió y qué hay ahora. */
export interface HuellaAuditoria {
  menciones: number;
  temas: number;
  ultimaResena: string | null;
}

export interface RespuestaAuditoria {
  auditoria: Auditoria | null;
  generadaEn?: string;
  generadaPor?: string;
  basadaEn?: HuellaAuditoria;
  ahora?: HuellaAuditoria | null;
  /** Han llegado reseñas o han cambiado los temas desde que se generó. */
  desactualizada?: boolean;
}

export interface MetaResenas {
  canales: { clave: CanalResena; etiqueta: string; escala: 5 | 10 }[];
  temas: { clave: string; etiqueta: string; accionable: boolean }[];
  estados: EstadoPlan[];
  severidades: SeveridadResena[];
  umbrales: { floja: number; mala: number; pesoPrevio: number; hoy: string };
}

/** Interruptor de una automatización de sereno-cron-jobs (ver server/src/services/automatizaciones.ts). */
export interface EstadoInterruptor {
  enabled: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

export interface InterruptoresAutomatizaciones {
  /** false mientras el cron no haya creado su contenedor: se ven, pero no se pueden mover */
  disponible: boolean;
  conInterruptor: string[];
  /** Solo las que alguien ha tocado; las demás están encendidas */
  estados: Record<string, EstadoInterruptor>;
}

/** Lo que el servidor saca del PDF de una factura de luz (calculadora de reparto). */
export interface FacturaLuzLeida {
  comercializadora: string | null;
  numero: string | null;
  periodo: string | null;
  desde: string | null;
  hasta: string | null;
  potencia: number;
  cuota: number;
  otros: number;
  energia: number;
  impuesto: number;
  ivaPct: number;
  kwh: number | null;
  total: number | null;
  avisos: string[];
}

/** Lecturas de los contadores de cada inquilino (calculadora de reparto de luz). */
export interface ContadorLuz {
  id: string;
  nombre: string;
  lectura: number;
  /** Si paga su parte de los costes fijos (un local vacío, no). */
  pagaFijos: boolean;
}

export interface LineaCierreLuz {
  contadorId: string;
  nombre: string;
  anterior: number;
  actual: number;
  kwh: number;
  pagaFijos: boolean;
  importe: number;
}

export interface CierreLuz {
  id: string;
  periodo: string;
  desde: string | null;
  hasta: string | null;
  totalFactura: number;
  guardado: string;
  usuario: string;
  lineas: LineaCierreLuz[];
}

export interface LecturasLuz {
  id: string;
  /** Última lectura guardada de cada contador: la anterior del mes que viene. */
  contadores: ContadorLuz[];
  /** Meses cerrados, del más antiguo al más reciente. */
  cierres: CierreLuz[];
  actualizado: string | null;
  actualizadoPor: string | null;
}

export interface NuevoCierreLuz {
  periodo: string;
  desde: string | null;
  hasta: string | null;
  totalFactura: number;
  lineas: {
    contadorId: string;
    nombre: string;
    anterior: number;
    actual: number;
    pagaFijos: boolean;
    importe: number;
  }[];
}

export const api = {
  // Huéspedes
  getHuespedes: (params: {
    q?: string; page?: number; pageSize?: number;
    orden?: string; dir?: 'asc' | 'desc';
    repetidores?: boolean; conLlamadas?: boolean; revisar?: boolean; excluidos?: boolean;
    canal?: string; alojamiento?: string; refrescar?: boolean;
  }) =>
    request<HuespedesListado>(
      `/huespedes?${new URLSearchParams({
        ...(params.q ? { q: params.q } : {}),
        ...(params.page ? { page: String(params.page) } : {}),
        ...(params.pageSize ? { pageSize: String(params.pageSize) } : {}),
        ...(params.orden ? { orden: params.orden } : {}),
        ...(params.dir ? { dir: params.dir } : {}),
        ...(params.repetidores ? { repetidores: '1' } : {}),
        ...(params.conLlamadas ? { conLlamadas: '1' } : {}),
        ...(params.revisar ? { revisar: '1' } : {}),
        ...(params.excluidos ? { excluidos: '1' } : {}),
        ...(params.canal ? { canal: params.canal } : {}),
        ...(params.alojamiento ? { alojamiento: params.alojamiento } : {}),
        ...(params.refrescar ? { refrescar: '1' } : {}),
      })}`,
    ),
  getHuespedesResumen: (refrescar = false) =>
    request<HuespedResumen>(`/huespedes/resumen${refrescar ? '?refrescar=1' : ''}`),
  getHuesped: (id: string) => request<HuespedDetalle>(`/huespedes/${encodeURIComponent(id)}`),
  excluirHuesped: (id: string, excluido: boolean, motivo?: string) =>
    request<{ excluido: boolean }>(`/huespedes/${encodeURIComponent(id)}/excluido`, {
      method: excluido ? 'PUT' : 'DELETE',
      ...(excluido ? { body: JSON.stringify(motivo ? { motivo } : {}) } : {}),
    }),

  // Centralita
  getContactos: () => request<Agenda>('/centralita/contactos'),
  /** Los huéspedes van aparte: son miles y solo se piden al abrir su pestaña. */
  getContactosHuespedes: () => request<Agenda>('/centralita/contactos/huespedes'),

  // Llamadas
  getLlamadasResumen: (from: string, to: string) =>
    request<LlamadasResumen>(`/llamadas/resumen?${new URLSearchParams({ from, to })}`),
  getLlamadas: (params: { from: string; to: string; q?: string; page?: number; pageSize?: number; direccion?: string; refrescar?: boolean }) =>
    request<LlamadasListado>(
      `/llamadas?${new URLSearchParams({
        from: params.from,
        to: params.to,
        ...(params.q ? { q: params.q } : {}),
        ...(params.direccion ? { direccion: params.direccion } : {}),
        ...(params.refrescar ? { refrescar: '1' } : {}),
        ...(params.page ? { page: String(params.page) } : {}),
        ...(params.pageSize ? { pageSize: String(params.pageSize) } : {}),
      })}`,
    ),
  getLlamada: (id: string) => request<LlamadaDetalle>(`/llamadas/${encodeURIComponent(id)}`),
  procesarLlamadaParaLead: (id: string, leadId: string) =>
    request<ResultadoProcesarLead>(`/llamadas/${encodeURIComponent(id)}/lead`, {
      method: 'POST',
      body: JSON.stringify({ leadId }),
    }),
  buscarEnTranscripciones: (q: string) =>
    request<LlamadasBusqueda>(`/llamadas/buscar?${new URLSearchParams({ q })}`),
  getLlamadasGuardadas: () => request<LlamadasGuardadas>('/llamadas/guardadas'),
  corregirLlamada: (id: string, campos: { guestName?: string | null; listingNickname?: string | null }) =>
    request<{ guestName: string | null; listingNickname: string | null }>(
      `/llamadas/${encodeURIComponent(id)}/correccion`,
      { method: 'PUT', body: JSON.stringify(campos) },
    ),
  quitarCorreccionLlamada: (id: string) =>
    request<{ ok: boolean }>(`/llamadas/${encodeURIComponent(id)}/correccion`, { method: 'DELETE' }),
  marcarLlamada: (id: string, guardada: boolean) =>
    request<{ guardada: boolean }>(`/llamadas/${encodeURIComponent(id)}/guardada`, {
      method: guardada ? 'PUT' : 'DELETE',
      ...(guardada ? { body: JSON.stringify({}) } : {}),
    }),

  // Aperturas
  getAperturasResumen: (from: string, to: string, smartlockId?: number) =>
    request<AperturasResumen>(
      `/aperturas/resumen?${new URLSearchParams({ from, to, ...(smartlockId ? { smartlockId: String(smartlockId) } : {}) })}`,
    ),
  getAperturasLlegadas: (from: string, to: string) =>
    request<AperturasLlegadas>(`/aperturas/llegadas?${new URLSearchParams({ from, to })}`),
  getAperturasManuales: (from: string, to: string) =>
    request<AperturasManuales>(`/aperturas/manuales?${new URLSearchParams({ from, to })}`),
  getAperturasSalud: (from: string, to: string) =>
    request<AperturasSalud>(`/aperturas/salud?${new URLSearchParams({ from, to })}`),
  getAperturasTimbres: (from: string, to: string) =>
    request<AperturasTimbres>(`/aperturas/timbres?${new URLSearchParams({ from, to })}`),

  // Auth

  // Owners
  getOwners: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/owners${qs}`);
  },
  getOwner: (id: string) => request<any>(`/owners/${id}`),
  createOwner: (data: any) => request<any>('/owners', { method: 'POST', body: JSON.stringify(data) }),
  updateOwner: (id: string, data: any) => request<any>(`/owners/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteOwner: (id: string) => request<void>(`/owners/${id}`, { method: 'DELETE' }),

  // Leads
  getLeads: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/leads${qs}`);
  },
  getLead: (id: string) => request<any>(`/leads/${id}`),
  createLead: (data: any) => request<any>('/leads', { method: 'POST', body: JSON.stringify(data) }),
  updateLead: (id: string, data: any) => request<any>(`/leads/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLead: (id: string) => request<void>(`/leads/${id}`, { method: 'DELETE' }),
  convertLead: (id: string) => request<{ success: boolean; ownerId: string }>(`/leads/${id}/convert`, { method: 'POST' }),

  // Peticiones de propietarios (llegan del wizard de tusereno.es)
  getPeticiones: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<Peticion[]>(`/peticiones${qs}`);
  },
  getPeticion: (id: string) => request<Peticion>(`/peticiones/${id}`),
  getConversacionesSerena: () => request<ConversacionSerenaResumen[]>('/serena/conversaciones'),
  getConversacionSerena: (id: string) => request<ConversacionSerenaDetalle>(`/serena/conversaciones/${id}`),
  aceptarPeticion: (id: string, nota?: string) =>
    request<Peticion>(`/peticiones/${id}/aceptar`, { method: 'POST', body: JSON.stringify({ nota }) }),
  rechazarPeticion: (id: string, nota?: string) =>
    request<Peticion>(`/peticiones/${id}/rechazar`, { method: 'POST', body: JSON.stringify({ nota }) }),
  reenviarCorreoPeticion: (id: string) =>
    request<Peticion>(`/peticiones/${id}/reenviar-correo`, { method: 'POST' }),
  /** URL de una foto: la sirve el servidor como proxy porque el blob es privado. */
  urlFotoPeticion: (id: string, indice: number) => `/api/peticiones/${id}/fotos/${indice}`,

  // ─── Candidatos (bolsa de empleo) ──────────────────────────────────────

  getCandidatos: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<Candidato[]>(`/candidatos${qs}`);
  },
  getContadoresCandidatos: () => request<ContadoresCandidatos>('/candidatos/contadores'),
  getCandidato: (id: string) => request<Candidato>(`/candidatos/${id}`),
  buscarDuplicadosCandidato: (datos: { email?: string; telefono?: string; excluirId?: string }) =>
    request<DuplicadoCandidato[]>('/candidatos/duplicados', {
      method: 'POST',
      body: JSON.stringify(datos),
    }),
  crearCandidato: (datos: Record<string, unknown>, forzar = false) =>
    request<Candidato>(`/candidatos${forzar ? '?forzar=1' : ''}`, {
      method: 'POST',
      body: JSON.stringify(datos),
    }),
  actualizarCandidato: (id: string, datos: Record<string, unknown>) =>
    request<Candidato>(`/candidatos/${id}`, { method: 'PUT', body: JSON.stringify(datos) }),
  cambiarEstadoCandidato: (id: string, cambio: CambioEstadoCandidato) =>
    request<Candidato>(`/candidatos/${id}/estado`, { method: 'POST', body: JSON.stringify(cambio) }),
  borrarCandidato: (id: string) => request<void>(`/candidatos/${id}`, { method: 'DELETE' }),
  /** Solo cuando el candidato ha pedido seguir en la bolsa: `via` es por dónde. */
  renovarCandidato: (id: string, via: ViaRenovacion) =>
    request<Candidato>(`/candidatos/${id}/renovar`, { method: 'POST', body: JSON.stringify({ via }) }),
  registrarActividadCandidato: (
    id: string,
    datos: { activityType: string; title?: string; description?: string },
  ) => request<{ ok: true }>(`/candidatos/${id}/actividad`, { method: 'POST', body: JSON.stringify(datos) }),

  /** Alta subiendo el CV: responde sin esperar a la IA, que va aparte. */
  crearCandidatoDesdeCv: (file: File, datos: Record<string, string>) => {
    const formData = new FormData();
    formData.append('file', file);
    for (const [k, v] of Object.entries(datos)) if (v) formData.append(k, v);
    return request<Candidato>('/candidatos/desde-cv', { method: 'POST', body: formData });
  },
  /**
   * Relanza la lectura del CV. Devuelve la ficha ya marcada como «leyendo»: el
   * resultado llega detrás y la pantalla lo va consultando.
   */
  leerCvConIa: (id: string) => request<Candidato>(`/candidatos/${id}/ia`, { method: 'POST' }),
  aplicarPropuestaIa: (id: string) =>
    request<{ candidato: Candidato; rellenados: string[] }>(`/candidatos/${id}/ia/aplicar`, {
      method: 'POST',
    }),

  subirDocumentosCandidato: (id: string, files: File[], tipo: string) => {
    const formData = new FormData();
    for (const f of files) formData.append('files', f);
    formData.append('tipo', tipo);
    return request<Candidato>(`/candidatos/${id}/documentos`, { method: 'POST', body: formData });
  },
  /** El blob es privado: lo sirve el servidor haciendo de proxy. */
  urlDocumentoCandidato: (id: string, docId: string, descargar = false) =>
    `/api/candidatos/${id}/documentos/${docId}${descargar ? '?mode=download' : ''}`,
  actualizarDocumentoCandidato: (
    id: string,
    docId: string,
    datos: { fileName?: string; tipo?: string; principal?: boolean },
  ) =>
    request<Candidato>(`/candidatos/${id}/documentos/${docId}`, {
      method: 'PATCH',
      body: JSON.stringify(datos),
    }),
  borrarDocumentoCandidato: (id: string, docId: string) =>
    request<Candidato>(`/candidatos/${id}/documentos/${docId}`, { method: 'DELETE' }),

  eliminarCandidatosEnLote: (ids: string[]) =>
    request<{ eliminados: number }>('/candidatos/lote/eliminar', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  exportCandidatosUrl: (params?: Record<string, string>) => {
    const qs = params && Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
    return `/api/export/candidatos${qs}`;
  },

  // Guest Portal (lectura de la base `guest_portal`, del portal del huésped)
  getGuestPortalLeads: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/guest-portal/leads${qs}`);
  },
  getGuestPortalServiceSales: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/guest-portal/service-sales${qs}`);
  },

  // Properties
  getProperties: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/properties${qs}`);
  },
  getProperty: (id: string) => request<any>(`/properties/${id}`),
  createProperty: (data: any) => request<any>('/properties', { method: 'POST', body: JSON.stringify(data) }),
  updateProperty: (id: string, data: any) => request<any>(`/properties/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProperty: (id: string) => request<void>(`/properties/${id}`, { method: 'DELETE' }),
  syncProperties: () => request<{ success: boolean; created: number; updated: number; deactivated: number; unchanged: number; errors: string[] }>('/admin/sync-properties', { method: 'POST' }),
  getPropertyPhotos: (id: string) => request<{ id: number; url: string; thumbnail?: string; caption?: string; sort_order?: number }[]>(`/properties/${id}/hostify-photos`),
  getHostifyDetails: (id: string) => request<{ bedrooms: number; bathrooms: number; beds: number; area: number; floor: number | null; personCapacity: number; hasPool: boolean; hasTerrace: boolean; hasParking: boolean; hasAC: boolean; hasWifi: boolean; elevator: boolean } | null>(`/properties/${id}/hostify-details`),
  getRoomDistribution: (id: string) => request<{
    rooms: { roomId: string; name: string; roomType: string; personCapacity: number; beds: { tipo: string; tamano: number; almohadas: { cantidad: number; tamano: number } }[]; items: { tipo: string; tamano?: number; cantidad: number }[] }[];
    notes: string | null;
    fullSeason: boolean;
    activityStartDate: string | null;
    activityEndDate: string | null;
    updatedAt: string | null;
  } | null>(`/properties/${id}/room-distribution`),

  // Activities
  getActivities: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/activities${qs}`);
  },
  createActivity: (data: any) => request<any>('/activities', { method: 'POST', body: JSON.stringify(data) }),
  deleteActivity: (id: string) => request<void>(`/activities/${id}`, { method: 'DELETE' }),

  // Quién es quién detrás de los `createdBy` (ver lib/autores.ts)
  getUsuarios: () => request<{ id: string; email: string; nombre: string | null }[]>('/usuarios'),

  // Tasks
  getTasks: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/tasks${qs}`);
  },
  getTask: (id: string) => request<any>(`/tasks/${id}`),
  /** Contadores para el aviso del menú y el dashboard. */
  getTasksResumen: () => request<any>('/tasks/resumen'),
  createTask: (data: any) => request<any>('/tasks', { method: 'POST', body: JSON.stringify(data) }),
  updateTask: (id: string, data: any) => request<any>(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTask: (id: string) => request<void>(`/tasks/${id}`, { method: 'DELETE' }),
  completeTask: (id: string) => request<any>(`/tasks/${id}/completar`, { method: 'POST' }),
  reopenTask: (id: string) => request<any>(`/tasks/${id}/reabrir`, { method: 'POST' }),
  /** Mueve el vencimiento N días; desde hoy si ya venció o no tenía fecha. */
  postponeTask: (id: string, dias: number) =>
    request<any>(`/tasks/${id}/aplazar`, { method: 'POST', body: JSON.stringify({ dias }) }),
  /** Una sola llamada para varias tareas: la lista no queda a medio actualizar. */
  bulkTasks: (ids: string[], accion: 'completar' | 'reabrir' | 'aplazar' | 'eliminar', dias?: number) =>
    request<{ hechas: string[]; fallidas: string[] }>('/tasks/lote', {
      method: 'POST',
      body: JSON.stringify({ ids, accion, ...(dias !== undefined ? { dias } : {}) }),
    }),

  // Pipeline stages
  getStages: () => request<any[]>('/pipeline-stages'),
  createStage: (data: any) => request<any>('/pipeline-stages', { method: 'POST', body: JSON.stringify(data) }),
  updateStage: (id: string, data: any) => request<any>(`/pipeline-stages/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteStage: (id: string) => request<void>(`/pipeline-stages/${id}`, { method: 'DELETE' }),

  // Settings
  getSettings: () => request<any[]>('/settings'),
  getSetting: (id: string) => request<any>(`/settings/${id}`),
  updateSetting: (id: string, data: any) => request<any>(`/settings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Automatizaciones (interruptores del cron)
  getInterruptores: () => request<InterruptoresAutomatizaciones>('/automatizaciones/interruptores'),
  cambiarInterruptor: (id: string, enabled: boolean) =>
    request<EstadoInterruptor>(`/automatizaciones/interruptores/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  // Dashboard
  getDashboard: () => request<any>('/dashboard'),
  // Lo pendiente de todo el CRM: el bloque «Pendiente» del Dashboard y los
  // números del menú. `fresco` salta la caché de minuto y medio del servidor.
  getAtencion: (fresco = false) => request<import('./atencion').Atencion>(`/atencion${fresco ? '?fresco=1' : ''}`),

  // Coupons (tusereno.es — sereno_shared/coupons)
  getCoupons: () => request<any[]>('/coupons'),
  getCoupon: (code: string) => request<any>(`/coupons/${encodeURIComponent(code)}`),
  createCoupon: (data: any) => request<any>('/coupons', { method: 'POST', body: JSON.stringify(data) }),
  updateCoupon: (code: string, data: any) => request<any>(`/coupons/${encodeURIComponent(code)}`, { method: 'PUT', body: JSON.stringify(data) }),
  setCouponActive: (code: string, active: boolean) => request<any>(`/coupons/${encodeURIComponent(code)}/active`, { method: 'PATCH', body: JSON.stringify({ active }) }),
  resetCouponRedemptions: (code: string) => request<any>(`/coupons/${encodeURIComponent(code)}/reset-redemptions`, { method: 'POST' }),
  deleteCoupon: (code: string) => request<void>(`/coupons/${encodeURIComponent(code)}`, { method: 'DELETE' }),

  // Gastos de mantenimiento (sereno_lavanderia/maintenance_expenses_v2)
  //
  // El `propertyId` viaja en la query en las escrituras porque es la clave de
  // partición del contenedor: sin él no hay lectura directa del documento.
  getGastos: (params?: {
    propertyId?: string; startDate?: string; endDate?: string;
    category?: string; status?: string; limit?: number;
  }) => {
    const qs = new URLSearchParams(
      Object.entries(params || {})
        .filter(([, v]) => v !== undefined && v !== '' && v !== 'all')
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    return request<ListadoGastos>(`/gastos${qs ? `?${qs}` : ''}`);
  },
  getGastoPropiedades: () => request<PropiedadGasto[]>('/gastos/propiedades'),
  crearGasto: (data: DatosGasto) =>
    request<Gasto>('/gastos', { method: 'POST', body: JSON.stringify(data) }),
  actualizarGasto: (id: string, propertyId: string, data: Partial<DatosGasto> & { chargedAmount?: number }) =>
    request<Gasto>(`/gastos/${encodeURIComponent(id)}?${new URLSearchParams({ propertyId })}`, {
      method: 'PUT', body: JSON.stringify(data),
    }),
  cobrarGasto: (id: string, propertyId: string, chargedAmount: number) =>
    request<Gasto>(`/gastos/${encodeURIComponent(id)}/cobrar?${new URLSearchParams({ propertyId })}`, {
      method: 'POST', body: JSON.stringify({ chargedAmount }),
    }),
  descobrarGasto: (id: string, propertyId: string) =>
    request<Gasto>(`/gastos/${encodeURIComponent(id)}/pendiente?${new URLSearchParams({ propertyId })}`, {
      method: 'POST',
    }),
  borrarGasto: (id: string, propertyId: string) =>
    request<void>(`/gastos/${encodeURIComponent(id)}?${new URLSearchParams({ propertyId })}`, {
      method: 'DELETE',
    }),

  // Personal — fichas de empleado (sereno_crm/empleados), enlazadas con la app
  // de lavandería por `lavanderiaId`.
  //
  // Los campos sensibles (DNI, NSS, IBAN, salarios) NO vienen en `Empleado`:
  // se piden con `getDatosSensibles`, y esa lectura queda registrada en la
  // actividad de la ficha.
  getPersonal: (params?: {
    q?: string; situacion?: string; puesto?: string; zona?: string; incluirCuentas?: boolean;
  }) => {
    const qs = new URLSearchParams(
      Object.entries(params || {})
        .filter(([, v]) => v !== undefined && v !== '' && v !== false)
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    return request<ListadoPersonal>(`/personal${qs ? `?${qs}` : ''}`);
  },
  getEmpleado: (id: string) => request<FichaEmpleado>(`/personal/${id}`),
  getDatosSensibles: (id: string) => request<DatosSensibles>(`/personal/${id}/sensible`),
  crearEmpleado: (datos: Record<string, unknown>) =>
    request<{ empleado: Empleado; aviso: string | null }>('/personal', {
      method: 'POST', body: JSON.stringify(datos),
    }),
  actualizarEmpleado: (id: string, datos: Record<string, unknown>) =>
    request<{ empleado: Empleado }>(`/personal/${id}`, { method: 'PUT', body: JSON.stringify(datos) }),
  borrarEmpleado: (id: string) =>
    request<{ borrado: boolean; ficheros: number }>(`/personal/${id}`, { method: 'DELETE' }),
  cambiarSituacion: (id: string, datos: { situacion: string; fecha: string; motivo?: string; nota?: string }) =>
    request<{ empleado: Empleado; aviso: string | null }>(`/personal/${id}/situacion`, {
      method: 'POST', body: JSON.stringify(datos),
    }),
  enlazarConLavanderia: (id: string, lavanderiaId?: string) =>
    request<{ empleado: Empleado }>(`/personal/${id}/enlazar`, {
      method: 'POST', body: JSON.stringify({ lavanderiaId }),
    }),

  // Los que existen en la lavandería y aún no tienen ficha aquí
  getPersonalSinFicha: () => request<EmpleadoSinFicha[]>('/personal/sin-ficha'),
  importarPersonal: (lavanderiaId?: string) =>
    request<{ creados: number; yaExistian: number }>('/personal/importar', {
      method: 'POST', body: JSON.stringify({ lavanderiaId }),
    }),

  crearEmpleadoDesdeCandidato: (candidatoId: string) =>
    request<{ empleado: Empleado }>(`/personal/desde-candidato/${candidatoId}`, { method: 'POST' }),

  // Contratos laborales del empleado. El sufijo `Empleado` no sobra: los
  // `crearContrato`/`actualizarContrato` de más abajo son los del generador de
  // contratos de gestión con propietarios, que no tienen nada que ver.
  crearContratoEmpleado: (id: string, datos: Record<string, unknown>) =>
    request<{ empleado: Empleado }>(`/personal/${id}/contratos`, { method: 'POST', body: JSON.stringify(datos) }),
  actualizarContratoEmpleado: (id: string, contratoId: string, datos: Record<string, unknown>) =>
    request<{ empleado: Empleado }>(`/personal/${id}/contratos/${contratoId}`, {
      method: 'PUT', body: JSON.stringify(datos),
    }),
  borrarContratoEmpleado: (id: string, contratoId: string) =>
    request<{ empleado: Empleado }>(`/personal/${id}/contratos/${contratoId}`, { method: 'DELETE' }),

  // Ausencias
  crearAusencia: (id: string, datos: Record<string, unknown>) =>
    request<{ empleado: Empleado; vacaciones: SaldoVacaciones }>(`/personal/${id}/ausencias`, {
      method: 'POST', body: JSON.stringify(datos),
    }),
  actualizarAusencia: (id: string, ausenciaId: string, datos: Record<string, unknown>) =>
    request<{ empleado: Empleado; vacaciones: SaldoVacaciones }>(`/personal/${id}/ausencias/${ausenciaId}`, {
      method: 'PUT', body: JSON.stringify(datos),
    }),
  borrarAusencia: (id: string, ausenciaId: string) =>
    request<{ empleado: Empleado; vacaciones: SaldoVacaciones }>(`/personal/${id}/ausencias/${ausenciaId}`, {
      method: 'DELETE',
    }),
  getVacaciones: (id: string, anio: number) =>
    request<SaldoVacaciones>(`/personal/${id}/vacaciones?anio=${anio}`),

  // Vistas del equipo
  getVencimientos: () => request<{ vencimientos: Vencimiento[]; total: number }>('/personal/vencimientos'),
  getAusenciasEquipo: (mes: string) =>
    request<{ mes: string; desde: string; hasta: string; empleados: FilaAusencias[] }>(
      `/personal/ausencias?mes=${mes}`,
    ),
  getFichajesEquipo: (mes: string) =>
    request<{
      mes: string;
      empleados: (ResumenFichajes & { id: string; nombreCompleto: string; situacion: 'alta' | 'baja' })[];
    }>(`/personal/fichajes?mes=${mes}`),
  getFichajesEmpleado: (id: string, mes: string) =>
    request<ResumenFichajes>(`/personal/${id}/fichajes?mes=${mes}`),

  // Documentos del empleado
  subirDocumentoEmpleado: (id: string, file: File, tipo: string, caduca?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipo', tipo);
    if (caduca) formData.append('caduca', caduca);
    return request<{ empleado: Empleado }>(`/personal/${id}/documentos`, { method: 'POST', body: formData });
  },
  /** El blob es privado: lo sirve el servidor haciendo de proxy. */
  urlDocumentoEmpleado: (id: string, docId: string, descargar = false) =>
    `/api/personal/${id}/documentos/${docId}${descargar ? '?descargar=1' : ''}`,

  // ─── Dispositivos ───────────────────────────────────────────────────────
  // El inventario del hardware de las casas. Instalar, retirar y mover
  // escriben los custom fields de Hostify; si Hostify falla, el servidor
  // responde 502 y no guarda nada salvo que se mande `sinHostify`.
  getDispositivos: (params?: {
    q?: string; marca?: string; tipo?: string; estado?: string; modelo?: string; listingId?: string; incluirBajas?: boolean;
  }) => {
    const qs = new URLSearchParams(
      Object.entries(params || {})
        .filter(([, v]) => v !== undefined && v !== '' && v !== false)
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    return request<ListadoDispositivos>(`/dispositivos${qs ? `?${qs}` : ''}`);
  },
  getDispositivo: (id: string) => request<FichaDispositivo>(`/dispositivos/${id}`),
  getDispositivosStock: () => request<ResumenStock>('/dispositivos/stock'),
  getDispositivosIncidencias: (params: { desde: string; hasta: string; tipo?: string; origen?: string; listingId?: string }) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]),
    ).toString();
    return request<ResumenIncidencias>(`/dispositivos/incidencias?${qs}`);
  },
  getDispositivosAuditoria: (conCuentas = true) => request<AuditoriaDispositivos>(`/dispositivos/auditoria${conCuentas ? '' : '?cuentas=0'}`),
  adoptarDeHostify: (datos: { listingId: string; dispositivoId: string; campo: number }) =>
    request<AccionDispositivo>('/dispositivos/auditoria/adoptar', { method: 'POST', body: JSON.stringify(datos) }),
  escribirHostifyDispositivo: (id: string, listingId: string) =>
    request<AccionDispositivo>(`/dispositivos/${id}/hostify/escribir`, { method: 'POST', body: JSON.stringify({ listingId }) }),
  getDispositivosResumen: () => request<ContadorAtencion>('/dispositivos/resumen'),
  getHistorialDispositivo: (id: string) => request<EntradaHistorial[]>(`/dispositivos/${id}/historial`),
  getDispositivosPropiedades: () => request<OpcionPropiedad[]>('/dispositivos/propiedades'),
  getDispositivosPorListing: (listingId: string) => request<FilaDispositivo[]>(`/dispositivos/por-listing/${listingId}`),
  getDispositivosPorPropiedad: (propertyId: string) =>
    request<{ listing: OpcionPropiedad | null; dispositivos: FilaDispositivo[] }>(`/dispositivos/por-propiedad/${propertyId}`),
  sincronizarDispositivos: () => request<ResultadoSincronizacion>('/dispositivos/sincronizar', { method: 'POST' }),
  migrarDispositivos: (aplicar: boolean) =>
    request<InformeMigracion>('/dispositivos/migrar', { method: 'POST', body: JSON.stringify({ aplicar }) }),
  crearDispositivo: (datos: Record<string, unknown>) =>
    request<{ dispositivo: Dispositivo; dispositivos?: Dispositivo[] }>('/dispositivos', { method: 'POST', body: JSON.stringify(datos) }),
  actualizarDispositivo: (id: string, datos: Record<string, unknown>) =>
    request<{ dispositivo: Dispositivo }>(`/dispositivos/${id}`, { method: 'PUT', body: JSON.stringify(datos) }),
  instalarDispositivo: (id: string, datos: Record<string, unknown>) =>
    request<AccionDispositivo>(`/dispositivos/${id}/instalar`, { method: 'POST', body: JSON.stringify(datos) }),
  retirarDispositivo: (id: string, datos: Record<string, unknown>) =>
    request<AccionDispositivo>(`/dispositivos/${id}/retirar`, { method: 'POST', body: JSON.stringify(datos) }),
  moverDispositivo: (id: string, datos: Record<string, unknown>) =>
    request<AccionDispositivo>(`/dispositivos/${id}/mover`, { method: 'POST', body: JSON.stringify(datos) }),
  cambiarEstadoDispositivo: (id: string, datos: Record<string, unknown>) =>
    request<AccionDispositivo>(`/dispositivos/${id}/estado`, { method: 'POST', body: JSON.stringify(datos) }),
  registrarPilasDispositivo: (id: string, datos: Record<string, unknown>) =>
    request<AccionDispositivo>(`/dispositivos/${id}/pilas`, { method: 'POST', body: JSON.stringify(datos) }),
  registrarIncidenciaDispositivo: (id: string, datos: Record<string, unknown>) =>
    request<AccionDispositivo>(`/dispositivos/${id}/incidencias`, { method: 'POST', body: JSON.stringify(datos) }),
  anadirNotaDispositivo: (id: string, texto: string) =>
    request<AccionDispositivo>(`/dispositivos/${id}/notas`, { method: 'POST', body: JSON.stringify({ texto }) }),
  enlazarDispositivo: (id: string, enlazadoA: string | null) =>
    request<AccionDispositivo>(`/dispositivos/${id}/enlace`, { method: 'POST', body: JSON.stringify({ enlazadoA }) }),
  borrarEntradaDispositivo: (id: string, entradaId: string) =>
    request<AccionDispositivo>(`/dispositivos/${id}/historial/${entradaId}`, { method: 'DELETE' }),
  consultarDispositivo: (id: string) => request<AccionDispositivo>(`/dispositivos/${id}/consultar`, { method: 'POST' }),
  reintentarHostifyDispositivo: (id: string) =>
    request<AccionDispositivo>(`/dispositivos/${id}/hostify/reintentar`, { method: 'POST' }),
  subirDocumentoDispositivo: (id: string, file: File, tipo: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipo', tipo);
    return request<AccionDispositivo>(`/dispositivos/${id}/documentos`, { method: 'POST', body: formData });
  },
  borrarDocumentoDispositivo: (id: string, docId: string) =>
    request<AccionDispositivo>(`/dispositivos/${id}/documentos/${docId}`, { method: 'DELETE' }),
  urlDocumentoDispositivo: (id: string, docId: string, descargar = false) =>
    `/api/dispositivos/${id}/documentos/${docId}${descargar ? '?descargar=1' : ''}`,
  borrarDocumentoEmpleado: (id: string, docId: string) =>
    request<{ empleado: Empleado }>(`/personal/${id}/documentos/${docId}`, { method: 'DELETE' }),

  // Copy — textos de los anuncios (lo que era copy.tusereno.es)
  //
  // Las rutas de traducción no devuelven el texto para que lo guarde el
  // cliente: el servidor traduce Y guarda en Hostify, y contesta con `saved` y
  // `errors` por idioma. Una tanda a medias se reintenta mirando `errors`, sin
  // repetir lo que ya entró.
  getCopyListings: () =>
    request<{ success: boolean; listings: CopyListing[]; total: number }>('/copy/listings'),
  getCopyListing: (id: number, includeRelated = true) =>
    request<any>(`/copy/listings/${id}?include_related_objects=${includeRelated ? 1 : 0}`),
  getCopyTranslations: (id: number) => request<any>(`/copy/listings/${id}/translations`),
  createCopyTranslations: (id: number, payload: CopyTranslationPayload[]) =>
    request<any>(`/copy/listings/${id}/translations`, { method: 'POST', body: JSON.stringify(payload) }),
  updateCopyTranslations: (id: number, payload: CopyTranslationPayload[]) =>
    request<any>(`/copy/listings/${id}/translations`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteCopyTranslations: (id: number, langs: string[]) =>
    request<any>(`/copy/listings/${id}/translations`, { method: 'DELETE', body: JSON.stringify(langs) }),

  generateCopyField: (id: number, body: {
    field: string;
    language: string;
    current_value?: string;
    include_amenities?: boolean;
    include_photos?: boolean;
    extra_context?: string;
  }) => request<{ success: boolean; text: string }>(`/copy/listings/${id}/ai/field`, {
    method: 'POST', body: JSON.stringify(body),
  }),
  generateCopyTitles: (id: number, body: { language: string; suggestions?: string }) =>
    request<{ success: boolean; titles: string[] }>(`/copy/listings/${id}/ai/titles`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  translateCopyField: (id: number, body: {
    field: string;
    source_lang: string;
    source_text: string;
    target_langs: string[];
  }) => request<CopyTranslateFieldResult>(`/copy/listings/${id}/ai/translate-field`, {
    method: 'POST', body: JSON.stringify(body),
  }),
  translateCopyAllFields: (id: number, body: {
    source_lang: string;
    fields: Record<string, string>;
    target_langs: string[];
  }) => request<CopyTranslateAllResult>(`/copy/listings/${id}/ai/translate-all`, {
    method: 'POST', body: JSON.stringify(body),
  }),

  getCopyPrompts: () => request<Record<string, CopyPrompt>>('/copy/prompts'),
  saveCopyPrompts: (prompts: Record<string, string>) =>
    request<{ success: boolean; saved_keys: string[] }>('/copy/prompts', {
      method: 'PUT', body: JSON.stringify(prompts),
    }),

  getCopyHistory: (id: number, limit?: number) =>
    request<{ success: boolean; history: CopyHistoryEntry[] }>(
      `/copy/listings/${id}/history${limit ? `?limit=${limit}` : ''}`
    ),
  getCopyHistoryEntry: (id: number, entryId: string) =>
    request<{ success: boolean; entry: CopyHistoryEntry }>(`/copy/listings/${id}/history/${entryId}`),
  getCopyOptimized: () =>
    request<{ success: boolean; optimized_ids: number[] }>('/copy/optimized'),

  // Analytics
  getAnalyticsFilters: () =>
    request<import('./types').AnalyticsFiltersData>('/analytics/filters'),
  getAnalytics: (params: {
    from: string;
    to: string;
    propertyIds?: string[];
    zones?: string[];
    channels?: string[];
    includeOwner?: boolean;
    activeOnly?: boolean;
    excludeFamily?: boolean;
    alicante?: import('./types').ModoAlicante;
    prorate?: boolean;
  }) => {
    const qs = new URLSearchParams();
    qs.set('from', params.from);
    qs.set('to', params.to);
    if (params.propertyIds?.length) qs.set('propertyIds', params.propertyIds.join(','));
    if (params.zones?.length) qs.set('zones', params.zones.join(','));
    if (params.channels?.length) qs.set('channels', params.channels.join(','));
    if (params.includeOwner) qs.set('includeOwner', 'true');
    if (params.activeOnly) qs.set('activeOnly', 'true');
    if (params.excludeFamily) qs.set('excludeFamily', 'true');
    if (params.alicante && params.alicante !== 'todas') qs.set('alicante', params.alicante);
    if (params.prorate === false) qs.set('prorate', 'false');
    return request<import('./types').AnalyticsResult>(`/analytics?${qs.toString()}`);
  },
  getPerformance: (params: {
    from: string;
    to: string;
    propertyIds?: string[];
    zones?: string[];
    channels?: string[];
    includeOwner?: boolean;
    activeOnly?: boolean;
    excludeFamily?: boolean;
    alicante?: import('./types').ModoAlicante;
    prorate?: boolean;
  }) => {
    const qs = new URLSearchParams();
    qs.set('from', params.from);
    qs.set('to', params.to);
    if (params.propertyIds?.length) qs.set('propertyIds', params.propertyIds.join(','));
    if (params.zones?.length) qs.set('zones', params.zones.join(','));
    if (params.channels?.length) qs.set('channels', params.channels.join(','));
    if (params.includeOwner) qs.set('includeOwner', 'true');
    if (params.activeOnly) qs.set('activeOnly', 'true');
    if (params.excludeFamily) qs.set('excludeFamily', 'true');
    if (params.alicante && params.alicante !== 'todas') qs.set('alicante', params.alicante);
    if (params.prorate === false) qs.set('prorate', 'false');
    return request<import('./types').PerformanceResult>(`/analytics/performance?${qs.toString()}`);
  },
  // Finance > Resumen: lo que factura Sereno (la cifra de Holded)
  getFacturacionSereno: (params: {
    from: string;
    to: string;
    propertyIds?: string[];
    zones?: string[];
    channels?: string[];
    activeOnly?: boolean;
    excludeFamily?: boolean;
    alicante?: import('./types').ModoAlicante;
  }) => {
    const qs = new URLSearchParams();
    qs.set('from', params.from);
    qs.set('to', params.to);
    if (params.propertyIds?.length) qs.set('propertyIds', params.propertyIds.join(','));
    if (params.zones?.length) qs.set('zones', params.zones.join(','));
    if (params.channels?.length) qs.set('channels', params.channels.join(','));
    if (params.activeOnly) qs.set('activeOnly', 'true');
    if (params.excludeFamily) qs.set('excludeFamily', 'true');
    if (params.alicante && params.alicante !== 'todas') qs.set('alicante', params.alicante);
    return request<import('./types').FacturacionSerenoResult>(`/analytics/facturacion-sereno?${qs.toString()}`);
  },
  compareAnalytics: (params: {
    fromA: string;
    toA: string;
    labelA: string;
    fromB: string;
    toB: string;
    labelB: string;
    propertyIds?: string[];
    zones?: string[];
    channels?: string[];
    includeOwner?: boolean;
    activeOnly?: boolean;
    excludeFamily?: boolean;
    alicante?: import('./types').ModoAlicante;
    prorate?: boolean;
  }) => {
    const qs = new URLSearchParams();
    qs.set('fromA', params.fromA);
    qs.set('toA', params.toA);
    qs.set('labelA', params.labelA);
    qs.set('fromB', params.fromB);
    qs.set('toB', params.toB);
    qs.set('labelB', params.labelB);
    if (params.propertyIds?.length) qs.set('propertyIds', params.propertyIds.join(','));
    if (params.zones?.length) qs.set('zones', params.zones.join(','));
    if (params.channels?.length) qs.set('channels', params.channels.join(','));
    if (params.includeOwner) qs.set('includeOwner', 'true');
    if (params.activeOnly) qs.set('activeOnly', 'true');
    if (params.excludeFamily) qs.set('excludeFamily', 'true');
    if (params.alicante && params.alicante !== 'todas') qs.set('alicante', params.alicante);
    if (params.prorate === false) qs.set('prorate', 'false');
    return request<import('./types').CompareResult>(`/analytics/compare?${qs.toString()}`);
  },
  // Analytics > Reservas: from/to opcionales (por defecto, todo el histórico)
  getHistoricoReservas: (params: {
    from?: string;
    to?: string;
    propertyIds?: string[];
    zones?: string[];
    channels?: string[];
    includeOwner?: boolean;
    activeOnly?: boolean;
    excludeFamily?: boolean;
    alicante?: import('./types').ModoAlicante;
  }) => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.propertyIds?.length) qs.set('propertyIds', params.propertyIds.join(','));
    if (params.zones?.length) qs.set('zones', params.zones.join(','));
    if (params.channels?.length) qs.set('channels', params.channels.join(','));
    if (params.includeOwner) qs.set('includeOwner', 'true');
    if (params.activeOnly) qs.set('activeOnly', 'true');
    if (params.excludeFamily) qs.set('excludeFamily', 'true');
    if (params.alicante && params.alicante !== 'todas') qs.set('alicante', params.alicante);
    return request<import('./types').HistoricoReservasResult>(`/analytics/reservas?${qs.toString()}`);
  },

  // Files
  uploadFile: (file: File, ownerId?: string, propertyId?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (ownerId) formData.append('ownerId', ownerId);
    if (propertyId) formData.append('propertyId', propertyId);
    return request<any>('/files/upload', { method: 'POST', body: formData });
  },

  // Owner Portal
  getPortalStatus: (ownerId: string) => request<EstadoPortal>(`/owner-portal/${ownerId}`),
  enablePortal: (ownerId: string) => request<{ enabled: boolean; email: string; emailSecondary?: string | null; acceso: AccesoPortal }>(`/owner-portal/${ownerId}`, { method: 'POST' }),
  /** Enlace nuevo para crear la contraseña, por WhatsApp (o por correo si no sale); con `bienvenida`, también el correo. */
  sendPortalAccess: (ownerId: string, bienvenida: boolean) => request<{ acceso: AccesoPortal }>(`/owner-portal/${ownerId}/acceso`, { method: 'POST', body: JSON.stringify({ bienvenida }) }),
  togglePortal: (ownerId: string, enabled: boolean) => request<{ enabled: boolean; email: string }>(`/owner-portal/${ownerId}`, { method: 'PUT', body: JSON.stringify({ enabled }) }),
  resetPortalPassword: (ownerId: string, password?: string) => request<{ enabled: boolean; email: string; emailSecondary?: string | null; password: string }>(`/owner-portal/${ownerId}/reset-password`, { method: 'POST', body: JSON.stringify(password ? { password } : {}) }),

  // Documents
  getDocuments: (ownerId: string, category?: string) => {
    const params: Record<string, string> = { ownerId };
    if (category) params.category = category;
    const qs = '?' + new URLSearchParams(params).toString();
    return request<any[]>(`/documents${qs}`);
  },
  getPropertyDocuments: (propertyId: string, category?: string) => {
    const params: Record<string, string> = { propertyId };
    if (category) params.category = category;
    const qs = '?' + new URLSearchParams(params).toString();
    return request<any[]>(`/documents${qs}`);
  },
  uploadDocument: (file: File, ownerId: string, category: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('ownerId', ownerId);
    formData.append('category', category);
    return request<any>('/documents/upload', { method: 'POST', body: formData });
  },
  uploadPropertyDocument: (file: File, propertyId: string, category: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('propertyId', propertyId);
    formData.append('category', category);
    return request<any>('/documents/upload', { method: 'POST', body: formData });
  },
  downloadDocumentUrl: (id: string, ownerId: string) => `/api/documents/${id}/download?ownerId=${encodeURIComponent(ownerId)}`,
  deleteDocument: (id: string, ownerId: string) =>
    request<void>(`/documents/${id}?ownerId=${encodeURIComponent(ownerId)}`, { method: 'DELETE' }),
  renameDocument: (id: string, ownerId: string, fileName: string) =>
    request<any>(`/documents/${id}?ownerId=${encodeURIComponent(ownerId)}`, { method: 'PATCH', body: JSON.stringify({ fileName }) }),

  // Export
  exportOwners: () => `/api/export/owners`,
  exportProperties: () => `/api/export/properties`,

  // Generador de contratos
  getPlantillasContrato: () => request<ModeloPlantilla[]>('/contratos/plantillas'),
  getPlantillaContrato: (key: ClavePlantilla, version: number | 'publicada') =>
    request<PlantillaVersion>(`/contratos/plantillas/${key}/${version}`),
  crearBorradorPlantilla: (key: ClavePlantilla, desdeVersion?: number) =>
    request<PlantillaVersion>(`/contratos/plantillas/${key}/borrador`, { method: 'POST', body: JSON.stringify({ desdeVersion }) }),
  guardarPlantilla: (key: ClavePlantilla, version: number, data: Partial<Pick<PlantillaVersion, 'titulo' | 'nombre' | 'variables' | 'secciones' | 'notas'>>) =>
    request<PlantillaVersion>(`/contratos/plantillas/${key}/${version}`, { method: 'PUT', body: JSON.stringify(data) }),
  publicarPlantilla: (key: ClavePlantilla, version: number) =>
    request<PlantillaVersion>(`/contratos/plantillas/${key}/${version}/publicar`, { method: 'POST' }),
  eliminarBorradorPlantilla: (key: ClavePlantilla, version: number) =>
    request<void>(`/contratos/plantillas/${key}/${version}`, { method: 'DELETE' }),
  getDiffPlantilla: (key: ClavePlantilla, de: number, a: number) =>
    request<DiffPlantilla>(`/contratos/plantillas/${key}/diff?de=${de}&a=${a}`),
  marcarPreajusteRevisado: (id: string) => request<Preajuste>(`/contratos/preajustes/${id}/revisado`, { method: 'POST' }),
  getPreajustesContrato: (templateKey?: ClavePlantilla) =>
    request<Preajuste[]>(`/contratos/preajustes${templateKey ? `?templateKey=${templateKey}` : ''}`),
  crearPreajuste: (data: Partial<Condiciones> & { templateKey: ClavePlantilla; nombre: string; descripcion?: string }) =>
    request<Preajuste>('/contratos/preajustes', { method: 'POST', body: JSON.stringify(data) }),
  actualizarPreajuste: (id: string, data: Partial<Condiciones> & { nombre?: string; descripcion?: string }) =>
    request<Preajuste>(`/contratos/preajustes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  eliminarPreajuste: (id: string) => request<void>(`/contratos/preajustes/${id}`, { method: 'DELETE' }),
  getParteContrato: (tipo: TipoParte, id: string) => request<RespuestaParte>(`/contratos/parte/${tipo}/${id}`),
  getContratos: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<ContratoLigero[]>(`/contratos${qs}`);
  },
  getContrato: (id: string) => request<Contrato>(`/contratos/${id}`),
  crearContrato: (data: Record<string, unknown>) => request<Contrato>('/contratos', { method: 'POST', body: JSON.stringify(data) }),
  actualizarContrato: (id: string, data: Record<string, unknown>) =>
    request<Contrato>(`/contratos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  vistaPreviaContrato: (id: string) => request<DocumentoResuelto>(`/contratos/${id}/vista-previa`, { method: 'POST' }),
  generarContrato: (id: string) => request<Contrato>(`/contratos/${id}/generar`, { method: 'POST' }),
  duplicarContrato: (id: string, actualizarPlantilla = false) =>
    request<Contrato>(`/contratos/${id}/duplicar`, { method: 'POST', body: JSON.stringify({ actualizarPlantilla }) }),
  anularContrato: (id: string, motivo?: string) =>
    request<Contrato>(`/contratos/${id}/anular`, { method: 'POST', body: JSON.stringify({ motivo }) }),
  cambiarEstadoContrato: (id: string, estado: EstadoContrato, signatureId?: string) =>
    request<Contrato>(`/contratos/${id}/estado`, { method: 'POST', body: JSON.stringify({ estado, signatureId }) }),
  guardarPreajusteDesdeContrato: (id: string, nombre: string, descripcion?: string) =>
    request<Preajuste>(`/contratos/${id}/preajuste`, { method: 'POST', body: JSON.stringify({ nombre, descripcion }) }),
  eliminarContrato: (id: string) => request<void>(`/contratos/${id}`, { method: 'DELETE' }),
  contratoPdfUrl: (id: string, opts?: { download?: boolean }) => `/api/contratos/${id}/pdf${opts?.download ? '?download=1' : ''}`,
  contratoPdfFirmadoUrl: (id: string, opts?: { download?: boolean }) => `/api/contratos/${id}/pdf-firmado${opts?.download ? '?download=1' : ''}`,
  // Consulta sin firma: enlace de solo lectura, sin Click & Sign
  prepararConsultaContrato: (id: string) => request<PreparacionConsulta>(`/contratos/${id}/consultas/preparar`),
  crearConsultaContrato: (id: string, data: { email: string; nombre?: string | null; mensaje?: string | null; dias?: number; enviarCorreo: boolean }) =>
    request<{ contrato: Contrato; consultaId: string; url: string; correo: { estado: string; detalle?: string } }>(
      `/contratos/${id}/consultas`, { method: 'POST', body: JSON.stringify(data) },
    ),
  revocarConsultaContrato: (id: string, consultaId: string) =>
    request<Contrato>(`/contratos/${id}/consultas/${consultaId}/revocar`, { method: 'POST' }),
  urlConsultaContrato: (token: string) => `${window.location.origin}/api/consulta/${token}`,
  // Firma con Click & Sign
  prepararFirmaContrato: (id: string) => request<PreparacionFirma>(`/contratos/${id}/firma/preparar`),
  enviarContratoAFirma: (id: string, data: { configId: number; configNombre?: string | null; firmantes: FirmanteEntrada[]; recordar?: boolean }) =>
    request<Contrato>(`/contratos/${id}/enviar-firma`, { method: 'POST', body: JSON.stringify(data) }),
  sincronizarFirmaContrato: (id: string) => request<Contrato>(`/contratos/${id}/sincronizar-firma`, { method: 'POST' }),
  recordatoriosConfigFirma: (configId: number) => request<RecordatoriosConfig>(`/contratos/firma/configs/${configId}/recordatorios`),
  crearConfigFirmaConRecordatorios: (configId: number, dias: number[]) =>
    request<RecordatoriosConfig>(`/contratos/firma/configs/${configId}/recordatorios`, { method: 'POST', body: JSON.stringify({ dias }) }),
  getAjustesFirma: () => request<AjustesFirma>('/contratos/firma/ajustes'),
  guardarAjustesFirma: (data: AjustesFirma) => request<AjustesFirma>('/contratos/firma/ajustes', { method: 'PUT', body: JSON.stringify(data) }),

  // Signatures / Contracts
  getSignatures: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/signatures${qs}`);
  },
  // Ojo: los ids de Click & Sign tienen 17 dígitos y no caben en un number de JS
  // sin perder precisión. Se manejan siempre como string.
  getSignatureStatus: (id: string) => request<any>(`/signatures/${id}/status`),
  getSignatoryDetails: (signatureId: string, signatoryId: string) =>
    request<any>(`/signatures/${signatureId}/signatories/${signatoryId}`),
  getSignatureFiles: (id: string) => request<any[]>(`/signatures/${id}/files`),
  getSignatureDownload: (id: string, fileGroup?: string) => {
    const qs = fileGroup ? `?file_group=${fileGroup}` : '';
    return request<{ files: Array<{ filename: string; file_url: string }> }>(`/signatures/${id}/download${qs}`);
  },
  // El binario lo sirve el backend: el enlace directo de Click & Sign caduca.
  signaturePdfUrl: (id: string, opts?: { download?: boolean }) =>
    `/api/signatures/${id}/pdf${opts?.download ? '?download=1' : ''}`,
  sendSignatureReminder: (id: string, data: { signatory_id: string; channel: 'email' | 'sms'; message?: string }) =>
    request<{ ok: boolean }>(`/signatures/${id}/reminder`, { method: 'POST', body: JSON.stringify(data) }),
  cancelSignature: (id: string) =>
    request<{ ok: boolean }>(`/signatures/${id}`, { method: 'DELETE' }),

  // Contract-Owner Links
  getContractLinks: () => request<Record<string, { ownerId: string; ownerName: string }>>('/signatures/links/all'),
  getOwnerContracts: (ownerId: string) => request<any[]>(`/signatures/links/owner/${ownerId}`),
  linkContract: (signatureId: string, ownerId: string, ownerName: string, contractMeta?: { contractId?: string; configName?: string; signatureStatus?: string }) =>
    request<any>(`/signatures/${signatureId}/link`, { method: 'PUT', body: JSON.stringify({ ownerId, ownerName, ...contractMeta }) }),
  unlinkContract: (signatureId: string) =>
    request<void>(`/signatures/${signatureId}/link`, { method: 'DELETE' }),
  // Procesar contrato: datos del PDF firmado a la ficha del propietario
  proponerProcesadoContrato: (signatureId: string) =>
    request<PropuestaProcesado>(`/signatures/${signatureId}/procesar`),
  procesarContrato: (signatureId: string, seleccion: { claves: string[]; viviendas: Record<string, string | null> }) =>
    request<ResultadoProcesado>(`/signatures/${signatureId}/procesar`, { method: 'POST', body: JSON.stringify(seleccion) }),

  // Fetch all signatures paginating in 1-month windows (up to 12 months back)
  getAllSignatures: async (): Promise<any[]> => {
    const all: any[] = [];
    const now = new Date();
    const months = 12;
    for (let i = 0; i < months; i++) {
      const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
      try {
        const sigs = await api.getSignatures({
          start_date: String(Math.floor(start.getTime() / 1000)),
          end_date: String(Math.floor(end.getTime() / 1000)),
        });
        all.push(...sigs);
      } catch {
        // skip failed windows
      }
    }
    // Deduplicate by signature_id
    const seen = new Set<string>();
    return all.filter(s => {
      const id = String(s.signature_id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  },

  // Liquidaciones
  getLiquidaciones: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/liquidaciones${qs}`);
  },
  getLiquidacion: (id: string) => request<any>(`/liquidaciones/${id}`),
  getLiquidacionReservations: (ownerId: string, month: number, year: number) =>
    request<{ reservations: any[]; cancelledReservations?: any[] }>(`/liquidaciones/reservations?ownerId=${ownerId}&month=${month}&year=${year}`),
  recomputeReservations: (ownerId: string, reservas: any[]) =>
    request<{ reservations: any[] }>(`/liquidaciones/recompute-reservations`, { method: 'POST', body: JSON.stringify({ ownerId, reservas }) }),
  getLavanderiaExpenses: (ownerId: string) =>
    request<{ expenses: any[] }>(`/liquidaciones/lavanderia-expenses?ownerId=${ownerId}`),
  previewLiquidacion: (data: any) =>
    request<any>('/liquidaciones/preview', { method: 'POST', body: JSON.stringify(data) }),
  createLiquidacion: (data: any) =>
    request<any>('/liquidaciones', { method: 'POST', body: JSON.stringify(data) }),
  deleteLiquidacion: (id: string) =>
    request<void>(`/liquidaciones/${id}`, { method: 'DELETE' }),

  // Liquidaciones — Qonto payments
  qontoEstado: () => request<{ connected: boolean }>('/qonto/status'),
  payLiquidacion: (id: string, concept: string) =>
    request<any>(`/liquidaciones/${id}/pay`, { method: 'POST', body: JSON.stringify({ concept }) }),
  getLiquidacionPaymentStatus: (id: string) =>
    request<{ status: string; transfer: any }>(`/liquidaciones/${id}/payment-status`),
  // Pago registrado a mano, para lo que se cobró fuera de Qonto
  markLiquidacionPaid: (id: string, data?: { concept?: string; date?: string }) =>
    request<{ liquidacion: any }>(`/liquidaciones/${id}/mark-paid`, { method: 'POST', body: JSON.stringify(data || {}) }),
  unmarkLiquidacionPaid: (id: string) =>
    request<{ liquidacion: any }>(`/liquidaciones/${id}/unmark-paid`, { method: 'POST' }),

  // Liquidaciones — factura al propietario en Holded y envío por correo
  getFacturaHolded: (id: string) =>
    request<FacturaHoldedPreview>(`/liquidaciones/${id}/factura-holded`),
  crearFacturaHolded: (id: string) =>
    request<{ liquidacion: any }>(`/liquidaciones/${id}/factura-holded`, { method: 'POST' }),
  /** Corrige serie/número de la factura de propietario (explotación) sin rehacer la liquidación. */
  cambiarNumeroFactura: (id: string, data: { prefix: string; num: number; actualizarSiguiente: boolean }) =>
    request<{ liquidacion: any; owner: any | null }>(`/liquidaciones/${id}/numero-factura`, { method: 'PATCH', body: JSON.stringify(data) }),
  /** Emite el borrador: Holded le pone número y, en la cuenta real, va a Verifactu. */
  aprobarFacturaHolded: (id: string) =>
    request<{ liquidacion: any }>(`/liquidaciones/${id}/factura-holded/aprobar`, { method: 'POST' }),
  /** El PDF lo sirve el servidor haciendo de proxy de Holded. */
  urlPdfFacturaHolded: (id: string) => `/api/liquidaciones/${id}/factura-holded/pdf`,
  /** Adjunta a la factura de Holded la liquidación en PDF (gestión). */
  adjuntarLiquidacionHolded: (id: string, pdf: { nombre: string; base64: string }) =>
    request<{ liquidacion: any }>(`/liquidaciones/${id}/factura-holded/adjunto`, { method: 'POST', body: JSON.stringify(pdf) }),
  // Explotación: la factura del propietario registrada en Holded como compra
  getCompraHolded: (id: string) =>
    request<CompraHoldedPreview>(`/liquidaciones/${id}/compra-holded`),
  /** Crea la compra, le adjunta el PDF y la aprueba; `avisos` dice qué no salió. */
  registrarCompraHolded: (id: string, pdf: { nombre: string; base64: string }) =>
    request<{ liquidacion: any; avisos: string[] }>(`/liquidaciones/${id}/compra-holded`, { method: 'POST', body: JSON.stringify(pdf) }),
  /** Repite lo que faltó de la compra: adjuntar el PDF o aprobarla. */
  completarCompraHolded: (id: string, pdf?: { nombre: string; base64: string }) =>
    request<{ liquidacion: any; avisos: string[] }>(`/liquidaciones/${id}/compra-holded/completar`, { method: 'POST', body: JSON.stringify(pdf || {}) }),
  enviarEmailLiquidacion: (
    id: string,
    data: {
      para: string; asunto: string; texto: string; liquidacionPdf?: { nombre: string; base64: string }; adjuntarFactura: boolean;
      /** ISO. Con él no sale ahora: lo manda el servidor a esa hora */
      programadoPara?: string;
    },
  ) =>
    request<{ liquidacion: any }>(`/liquidaciones/${id}/enviar-email`, { method: 'POST', body: JSON.stringify(data) }),
  /** No mandar el correo programado (o descartar uno que no salió) */
  cancelarCorreoProgramado: (id: string) =>
    request<{ liquidacion: any }>(`/liquidaciones/${id}/correo-programado`, { method: 'DELETE' }),

  // Remesas
  getRemesas: (params?: { year?: number }) => {
    const qs = params?.year ? `?year=${params.year}` : '';
    return request<any[]>(`/remesas${qs}`);
  },
  getRemesa: (id: string) => request<any>(`/remesas/${id}`),
  generateRemesa: (data: { month: number; year: number }) =>
    request<any>('/remesas/generate', { method: 'POST', body: JSON.stringify(data) }),
  generateRemesaSSE: async (
    data: { month: number; year: number },
    onProgress: (info: { current: number; total: number; ownerName: string; status: string; skippedExpenses?: number }) => void,
  ): Promise<any> => {
    const res = await fetchConSesion('/remesas/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ error: 'Error' }));
      throw new Error(errBody.error || `HTTP ${res.status}`);
    }
    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalRemesa: any = null;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const parsed = JSON.parse(line.slice(6));
          if (parsed.done) { finalRemesa = parsed.remesa; }
          else if (parsed.error) { throw new Error(parsed.error); }
          else { onProgress(parsed); }
        } catch (e: any) {
          if (e.message && !e.message.startsWith('JSON')) throw e;
        }
      }
    }
    return finalRemesa;
  },
  deleteRemesa: (id: string, deleteLiquidaciones = false) =>
    request<void>(`/remesas/${id}?deleteLiquidaciones=${deleteLiquidaciones}`, { method: 'DELETE' }),
  getPendingPricingExpenses: () =>
    request<any[]>('/liquidaciones/pending-pricing'),
  updateExpensePrice: (data: { id: string; source: string; propertyId: string; amount: number }) =>
    request<any>('/liquidaciones/expense-price', { method: 'PATCH', body: JSON.stringify(data) }),
  archiveExpense: (data: { id: string; source: string; propertyId: string }) =>
    request<any>('/liquidaciones/archive-expense', { method: 'POST', body: JSON.stringify(data) }),

  // Invoices (Facturas a huéspedes)
  getInvoices: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any[]>(`/invoices${qs}`);
  },
  getInvoice: (id: string) => request<any>(`/invoices/${id}`),
  getInvoiceReservations: (ownerId: string, month: number, year: number) =>
    request<{ reservations: any[]; owner: any; invoiceSeries: any }>(`/invoices/reservations?ownerId=${ownerId}&month=${month}&year=${year}`),
  createInvoice: (data: any) =>
    request<any>('/invoices', { method: 'POST', body: JSON.stringify(data) }),
  deleteInvoice: (id: string, force = false) =>
    request<void>(`/invoices/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),
  pushInvoiceToHolded: (id: string) =>
    request<any>(`/invoices/${id}/holded`, { method: 'POST' }),
  getInvoiceSeries: () => request<any>('/invoices/series'),
  updateInvoiceSeries: (data: any) =>
    request<any>('/invoices/series', { method: 'PUT', body: JSON.stringify(data) }),
  getAllInvoiceReservations: (month: number, year: number) =>
    request<{ reservations: any[]; invoiceSeries: any }>(`/invoices/all-reservations?month=${month}&year=${year}`),

  /**
   * Streaming version: receives reservations progressively via SSE.
   * Calls onReservation for each reservation as it arrives.
   * Calls onProgress({ processed, total }) for progress updates.
   * Calls onDone({ invoiceSeries }) when complete.
   */
  streamInvoiceReservations: (
    month: number,
    year: number,
    onReservation: (r: any) => void,
    onProgress: (p: { processed: number; total: number }) => void,
    onDone: (meta: { invoiceSeries: any }) => void,
    signal?: AbortSignal,
    model: 'explotacion' | 'gestion' = 'explotacion',
  ): Promise<void> => {
    return fetchConSesion(`/invoices/all-reservations-stream?month=${month}&year=${year}&model=${model}`, {
      signal,
    }).then(async res => {
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          try {
            const ev = JSON.parse(part.slice(6));
            if (ev.type === 'total') onProgress({ processed: 0, total: ev.count });
            else if (ev.type === 'reservation') {
              onReservation(ev.data);
              onProgress({ processed: ev.processed, total: ev.total });
            } else if (ev.type === 'done') onDone({ invoiceSeries: ev.invoiceSeries });
          } catch { /* skip malformed event */ }
        }
      }
    });
  },

  // Analytics > Reseñas
  getResumenResenas: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<ResumenResenas>(`/reviews/resumen${qs}`);
  },
  getDetalleListing: (listingId: string, params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<DetalleListing>(`/reviews/listings/${listingId}${qs}`);
  },
  getPlanMejora: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<PlanMejora>(`/reviews/plan${qs}`);
  },
  guardarPuntoPlan: (listingId: string, tema: string, cambios: { estado?: EstadoPlan; nota?: string | null }) =>
    request<any>(`/reviews/plan/${listingId}/${tema}`, { method: 'PUT', body: JSON.stringify(cambios) }),
  borrarPuntoPlan: (listingId: string, tema: string) =>
    request<void>(`/reviews/plan/${listingId}/${tema}`, { method: 'DELETE' }),
  descartarMencion: (listingId: string, tema: string, reviewId: string) =>
    request<any>(`/reviews/plan/${listingId}/${tema}/descartes`, { method: 'POST', body: JSON.stringify({ reviewId }) }),
  recuperarMencion: (listingId: string, tema: string, reviewId: string) =>
    request<void>(`/reviews/plan/${listingId}/${tema}/descartes/${reviewId}`, { method: 'DELETE' }),
  getAuditoria: (listingId: string, params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<RespuestaAuditoria>(`/reviews/plan/${listingId}/auditoria${qs}`);
  },
  generarAuditoria: (listingId: string, params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<RespuestaAuditoria>(`/reviews/plan/${listingId}/auditoria${qs}`, { method: 'POST' });
  },
  borrarAuditoria: (listingId: string) =>
    request<void>(`/reviews/plan/${listingId}/auditoria`, { method: 'DELETE' }),
  getMetaResenas: () => request<MetaResenas>('/reviews/meta'),

  // ─── Comunicaciones (campañas de correo) ───────────────────────────────
  // Solo un borrador se edita: programar o enviar congela el contenido.
  getCampanas: () => request<Campana[]>('/comunicaciones'),
  getCampana: (id: string) => request<Campana>(`/comunicaciones/${id}`),
  crearCampana: (canal: Canal = 'email', nombre?: string) =>
    request<Campana>('/comunicaciones', { method: 'POST', body: JSON.stringify({ nombre, canal }) }),
  guardarCampana: (id: string, datos: CampanaEditable) =>
    request<Campana>(`/comunicaciones/${id}`, { method: 'PUT', body: JSON.stringify(datos) }),
  borrarCampana: (id: string) => request<void>(`/comunicaciones/${id}`, { method: 'DELETE' }),
  duplicarCampana: (id: string) => request<Campana>(`/comunicaciones/${id}/duplicar`, { method: 'POST' }),
  getRevisionCampana: (id: string) => request<Revision>(`/comunicaciones/${id}/revision`),
  enviarPruebaCampana: (id: string, para: string[], idioma: Idioma) =>
    request<{ idioma: Idioma; resultados: { para: string; estado: string; detalle?: string }[] }>(
      `/comunicaciones/${id}/prueba`, { method: 'POST', body: JSON.stringify({ para, idioma }) }),
  enviarPruebaWhatsApp: (id: string, telefonos: string[], idioma: string) =>
    request<{ idioma: string; aviso?: string; resultados: { para: string; estado: string; detalle?: string }[] }>(
      `/comunicaciones/${id}/prueba`, { method: 'POST', body: JSON.stringify({ telefonos, idioma }) }),
  programarCampana: (id: string, programadaPara: string, avisosAceptados: string[] = []) =>
    request<Campana>(`/comunicaciones/${id}/programar`, { method: 'POST', body: JSON.stringify({ programadaPara, avisosAceptados }) }),
  desprogramarCampana: (id: string) => request<Campana>(`/comunicaciones/${id}/desprogramar`, { method: 'POST' }),
  enviarCampana: (id: string, avisosAceptados: string[] = []) =>
    request<Campana & { total: number }>(`/comunicaciones/${id}/enviar`, { method: 'POST', body: JSON.stringify({ avisosAceptados }) }),
  pausarCampana: (id: string) => request<Campana>(`/comunicaciones/${id}/pausar`, { method: 'POST' }),
  reanudarCampana: (id: string) => request<Campana>(`/comunicaciones/${id}/reanudar`, { method: 'POST' }),
  cancelarCampana: (id: string) => request<Campana>(`/comunicaciones/${id}/cancelar`, { method: 'POST' }),
  getEnviosCampana: (id: string) => request<EnvioCampana[]>(`/comunicaciones/${id}/envios`),
  getEnlacesCampana: (id: string) => request<FilaEnlace[]>(`/comunicaciones/${id}/enlaces`),
  recuentoAudiencia: (audiencia: Audiencia, canal: Canal = 'email') =>
    request<Recuento>('/comunicaciones/recuento', { method: 'POST', body: JSON.stringify({ audiencia, canal }) }),
  vistaPreviaCampana: (version: Version, idioma: Idioma, tipo: TipoDestinatario) =>
    request<VistaPrevia>('/comunicaciones/vista-previa', { method: 'POST', body: JSON.stringify({ version, idioma, tipo }) }),
  subirImagenCampana: (fichero: File) => {
    const fd = new FormData();
    fd.append('imagen', fichero);
    return request<{ url: string }>('/comunicaciones/imagenes', { method: 'POST', body: fd });
  },
  getAjustesComunicaciones: () => request<AjustesComunicaciones>('/comunicaciones/ajustes'),
  guardarAjustesComunicaciones: (a: AjustesComunicaciones) =>
    request<AjustesComunicaciones>('/comunicaciones/ajustes', { method: 'PUT', body: JSON.stringify(a) }),
  getBajasComunicaciones: () => request<BajaComunicaciones[]>('/comunicaciones/bajas'),
  anadirBajaComunicaciones: (email: string, detalle?: string) =>
    request<{ ok: true }>('/comunicaciones/bajas', { method: 'POST', body: JSON.stringify({ email, detalle }) }),
  quitarBajaComunicaciones: (email: string) =>
    request<void>(`/comunicaciones/bajas/${encodeURIComponent(email)}`, { method: 'DELETE' }),
  // WhatsApp (API de Meta): conexión, plantillas y bandeja de conversaciones
  getEstadoWhatsApp: () => request<EstadoWhatsApp>('/comunicaciones/whatsapp/estado'),
  suscribirWhatsApp: () => request<{ ok: true }>('/comunicaciones/whatsapp/suscribir', { method: 'POST' }),
  getPlantillasWhatsApp: () => request<PlantillaResumen[]>('/comunicaciones/whatsapp/plantillas'),
  crearPlantillaWhatsApp: (b: BorradorPlantilla) =>
    request<{ id: string; estado: string; categoria?: string; avisos: string[] }>('/comunicaciones/whatsapp/plantillas', {
      method: 'POST', body: JSON.stringify(b),
    }),
  revisarPlantillaWhatsApp: (b: BorradorPlantilla) =>
    request<{ errores: string[]; avisos: string[] }>('/comunicaciones/whatsapp/plantillas/revision', { method: 'POST', body: JSON.stringify(b) }),
  borrarPlantillaWhatsApp: (nombre: string) =>
    request<void>(`/comunicaciones/whatsapp/plantillas/${encodeURIComponent(nombre)}`, { method: 'DELETE' }),
  /** `nombre`: el del destinatario en los huecos de nombre (por defecto, «Laura»). */
  vistaPreviaWhatsApp: (whatsapp: WhatsAppCampana, idioma: string, idiomaReserva: string, nombre?: string) =>
    request<{ idioma: string | null; previa: PreviaWhatsApp | null }>('/comunicaciones/whatsapp/vista-previa', {
      method: 'POST', body: JSON.stringify({ whatsapp, idioma, idiomaReserva, ...(nombre ? { nombre } : {}) }),
    }),
  getWhatsAppSinLeer: () => request<{ n: number }>('/comunicaciones/whatsapp/sin-leer'),
  getConversacionesWhatsApp: () => request<ConversacionWhatsApp[]>('/comunicaciones/whatsapp/conversaciones'),
  getConversacionWhatsApp: (telefono: string) =>
    request<HiloWhatsApp>(`/comunicaciones/whatsapp/conversaciones/${encodeURIComponent(telefono)}`),
  marcarLeidaWhatsApp: (telefono: string) =>
    request<{ ok: true }>(`/comunicaciones/whatsapp/conversaciones/${encodeURIComponent(telefono)}/leida`, { method: 'POST' }),
  marcarAtendidaWhatsApp: (telefono: string, atendida: boolean) =>
    request<{ ok: true }>(`/comunicaciones/whatsapp/conversaciones/${encodeURIComponent(telefono)}/atendida`, {
      method: 'POST', body: JSON.stringify({ atendida }),
    }),
  getWhatsAppDeLead: (leadId: string) => request<WhatsAppDeLead>(`/comunicaciones/whatsapp/leads/${encodeURIComponent(leadId)}`),
  enviarPlantillaALead: (leadId: string, plantilla: string, parametros: WhatsAppCampana['parametros']) =>
    request<MensajeWhatsApp>(`/comunicaciones/whatsapp/leads/${encodeURIComponent(leadId)}/plantilla`, {
      method: 'POST', body: JSON.stringify({ plantilla, parametros }),
    }),
  /** Desde Conversaciones: una plantilla del CRM a una conversación que ya existe (vale pasadas 24 h). */
  enviarPlantillaAConversacion: (telefono: string, plantilla: string, parametros: WhatsAppCampana['parametros']) =>
    request<MensajeWhatsApp>(`/comunicaciones/whatsapp/conversaciones/${encodeURIComponent(telefono)}/plantilla`, {
      method: 'POST', body: JSON.stringify({ plantilla, parametros }),
    }),
  /** La IA lee los mensajes nuevos y propone; no apunta nada. */
  procesarConversacionLead: (leadId: string) =>
    request<ProcesoConversacion>(`/comunicaciones/whatsapp/leads/${encodeURIComponent(leadId)}/procesar`, { method: 'POST' }),
  aceptarConversacionLead: (leadId: string, b: { desde: string; hasta: string; mensajes: number; propuesta: PropuestaConversacion; moverAFase: boolean }) =>
    request<{ actividadId: string; rellenados: string[]; faseMovida: string | null }>(`/comunicaciones/whatsapp/leads/${encodeURIComponent(leadId)}/procesar/aceptar`, {
      method: 'POST', body: JSON.stringify(b),
    }),
  responderWhatsApp: (telefono: string, texto: string) =>
    request<MensajeWhatsApp>(`/comunicaciones/whatsapp/conversaciones/${encodeURIComponent(telefono)}/mensajes`, {
      method: 'POST', body: JSON.stringify({ texto }),
    }),

  // Calculadoras
  /** Lee la factura de luz en PDF y devuelve sus importes. El PDF no se guarda. */
  leerFacturaLuz: (fichero: File) => {
    const fd = new FormData();
    fd.append('factura', fichero);
    return request<FacturaLuzLeida>('/calculadoras/luz/factura', { method: 'POST', body: fd });
  },
  getLecturasLuz: () => request<LecturasLuz>('/calculadoras/luz/lecturas'),
  /** Lecturas de partida de los contadores. No cierra ningún mes. */
  guardarLecturasLuz: (contadores: ContadorLuz[]) =>
    request<LecturasLuz>('/calculadoras/luz/lecturas', { method: 'PUT', body: JSON.stringify({ contadores }) }),
  cerrarMesLuz: (cierre: NuevoCierreLuz) =>
    request<LecturasLuz>('/calculadoras/luz/cierres', { method: 'POST', body: JSON.stringify(cierre) }),
  deshacerMesLuz: () => request<LecturasLuz>('/calculadoras/luz/cierres/ultimo', { method: 'DELETE' }),
};

// ── Portal del propietario ──────────────────────────────────────────────────

/** Lo que salió la última vez que se mandó el acceso. Ver server/src/services/owner-portal-acceso.ts. */
export interface AccesoPortal {
  en: string;
  por: string;
  caduca: string;
  whatsapp: {
    estado: 'enviado' | 'sent' | 'delivered' | 'read' | 'failed' | 'sin_telefono' | 'baja' | 'sin_plantilla' | 'error';
    telefono?: string;
    detalle?: string;
  };
  correo?: { estado: 'enviado' | 'error' | 'no_configurado'; para: string; conEnlace: boolean; detalle?: string };
}

export interface EstadoPortal {
  enabled: boolean;
  email: string | null;
  emailSecondary?: string | null;
  createdAt?: string;
  updatedAt?: string;
  passwordChangedAt?: string | null;
  acceso?: AccesoPortal | null;
  enlacePendiente?: { proposito: 'activation' | 'recovery'; caduca: string } | null;
  /** Adónde iría el acceso si se activa ahora. */
  destino?: { email: string | null; telefono: string | null };
}
